import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { incluyeTexto } from '../utils/texto';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import TaskMentionChip from './Proyectos/TaskMentionChip';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import {
    MessageCircle,
    Send,
    Paperclip,
    Search,
    Plus,
    Check,
    CheckCheck,
    File,
    FileText,
    FileSpreadsheet,
    FileType,
    FileArchive,
    Image,
    X,
    Users,
    Smile,
    Sticker,
    MoreVertical,
    ArrowLeft,
    Trash2,
    UserPlus,
    UserMinus,
    Settings,
    Crown,
    ShieldCheck,
    LogOut,
    ChevronUp,
    Ban,
    Reply,
    Pin,
    SmilePlus,
    Pencil,
    Forward,
    Circle,
    BarChart3,
    Palette,
    Zap,
    Sparkles,
    Ticket,
    Loader2,
    Monitor,
    Bookmark,
    BookmarkCheck,
    Bell,
    Clock3,
} from 'lucide-react';
import { Phone, Video as VideoIcon } from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import FileViewer from './FileViewer';
import { useCall } from '../context/CallContext';
import CallMessageBubble, { isCallMessage } from './Calls/CallMessageBubble';
import PdfThumbnail from './PdfThumbnail';
import DocPreview, { isDocPreviewable, isExcelFile } from './DocPreview';
import AiLoadingOverlay from './AiLoadingOverlay';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ChatView.css';

// Constantes de validación (sincronizadas con backend)
const MAX_MESSAGE_LENGTH = 10000;
const MAX_FILE_SIZE = 20 * 1024 * 1024;  // 20MB
const ALLOWED_EXTENSIONS = ['txt', 'pdf', 'png', 'jpg', 'jpeg', 'gif', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'rar'];
const MOBILE_CHAT_STORAGE_KEY = 'chat_mobile_open';
const LAST_CONVERSATION_STORAGE_KEY = 'chat_last_conversation_id';
const CHAT_DEBUG_TRAIL_KEY = 'chat_mobile_debug_trail';

const appendChatDebugTrail = (entry) => {
    try {
        const current = JSON.parse(sessionStorage.getItem(CHAT_DEBUG_TRAIL_KEY) || '[]');
        const next = [...current.slice(-79), entry];
        sessionStorage.setItem(CHAT_DEBUG_TRAIL_KEY, JSON.stringify(next));
    } catch {
        // no-op: debug only
    }
};

// Emojis rápidos para reacciones
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// Opciones de personalización del chat
// Paleta unificada y curada para burbujas. Dos vibes: tonos profundos (refinados),
// pasteles tenues (sofisticados) y gradientes monotonales (sin saltos cromáticos bruscos).
const BUBBLE_COLORS = [
    // Tonos profundos — refinados, no neón
    { key: 'default', label: 'Cyan', bg: '#0891b2', color: '#0891b2', text: '#fff' },
    { key: 'indigo', label: 'Índigo', bg: '#4338ca', color: '#4338ca', text: '#fff' },
    { key: 'plum', label: 'Ciruela', bg: '#7e22ce', color: '#7e22ce', text: '#fff' },
    { key: 'cherry', label: 'Cereza', bg: '#be123c', color: '#be123c', text: '#fff' },
    { key: 'terracotta', label: 'Terracota', bg: '#c2410c', color: '#c2410c', text: '#fff' },
    { key: 'forest', label: 'Bosque', bg: '#15803d', color: '#15803d', text: '#fff' },
    { key: 'charcoal', label: 'Carbón', bg: '#334155', color: '#334155', text: '#fff' },
    // Pasteles — texto tonificado al hue
    { key: 'gray', label: 'Perla', bg: '#f1f5f9', color: '#94a3b8', text: '#1e293b' },
    { key: 'cream', label: 'Crema', bg: '#fef3c7', color: '#d97706', text: '#78350f' },
    { key: 'sage', label: 'Salvia', bg: '#dcfce7', color: '#16a34a', text: '#14532d' },
    { key: 'sky', label: 'Cielo', bg: '#dbeafe', color: '#3b82f6', text: '#1e3a8a' },
    { key: 'mauve', label: 'Malva', bg: '#fbcfe8', color: '#db2777', text: '#831843' },
    { key: 'mist', label: 'Bruma', bg: '#e0e7ff', color: '#6366f1', text: '#3730a3' },
    // Gradientes — pares armónicos, sin saltos
    { key: 'sunset-grad', label: 'Atardecer', bg: 'linear-gradient(135deg, #fb923c 0%, #db2777 100%)', color: '#db2777', text: '#fff' },
    { key: 'aurora-grad', label: 'Aurora', bg: 'linear-gradient(135deg, #818cf8 0%, #c084fc 100%)', color: '#a78bfa', text: '#fff' },
    { key: 'marina-grad', label: 'Marina', bg: 'linear-gradient(135deg, #0e7490 0%, #1e40af 100%)', color: '#1e40af', text: '#fff' },
    { key: 'bronze-grad', label: 'Bronce', bg: 'linear-gradient(135deg, #fbbf24 0%, #c2410c 100%)', color: '#c2410c', text: '#fff' },
    { key: 'velvet-grad', label: 'Terciopelo', bg: 'linear-gradient(135deg, #7e22ce 0%, #be123c 100%)', color: '#7e22ce', text: '#fff' },
];

const BUBBLE_SHAPES = [
    { key: 'tail', label: 'Burbuja' },     // 16px, esquina con cola
    { key: 'rounded', label: 'Redonda' },  // 22px en todas las esquinas
    { key: 'squared', label: 'Cuadrada' }, // 6px en todas las esquinas
];

// Fondos curados en 3 grupos: neutrales sutiles, gradientes lineales y mallas radiales.
const CHAT_BACKGROUNDS = [
    // Neutrales sutiles
    { key: 'none', label: 'Sin fondo', style: {} },
    { key: 'pearl', label: 'Perla', style: { background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)' } },
    { key: 'linen', label: 'Lino', style: { background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' } },
    // Gradientes lineales — vibes tenues
    { key: 'dawn', label: 'Amanecer', style: { background: 'linear-gradient(135deg, #fce7f3 0%, #fed7aa 100%)' } },
    { key: 'dusk', label: 'Crepúsculo', style: { background: 'linear-gradient(135deg, #fae8ff 0%, #fbcfe8 100%)' } },
    { key: 'lagoon', label: 'Laguna', style: { background: 'linear-gradient(135deg, #cffafe 0%, #dbeafe 100%)' } },
    { key: 'meadow', label: 'Pradera', style: { background: 'linear-gradient(135deg, #dcfce7 0%, #cffafe 100%)' } },
    // Multi-stop — más expresivos
    { key: 'sunset', label: 'Atardecer', style: { background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 30%, #fdba74 60%, #fed7aa 100%)' } },
    { key: 'lavender', label: 'Lavanda', style: { background: 'linear-gradient(135deg, #fae8ff 0%, #e9d5ff 40%, #f5d0fe 70%, #fce7f3 100%)' } },
    { key: 'aurora', label: 'Aurora', style: { background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 25%, #c4b5fd 50%, #bfdbfe 75%, #a7f3d0 100%)' } },
    { key: 'ocean', label: 'Océano', style: { background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 30%, #a5f3fc 60%, #cffafe 100%)' } },
    { key: 'forest', label: 'Bosque', style: { background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 30%, #d9f99d 60%, #ecfccb 100%)' } },
    // Mallas radiales — atmosfera
    { key: 'mesh-warm', label: 'Malla cálida', style: { background: 'radial-gradient(at 20% 30%, #fef3c7 0%, transparent 50%), radial-gradient(at 80% 20%, #fce7f3 0%, transparent 50%), radial-gradient(at 50% 80%, #fed7aa 0%, transparent 50%), radial-gradient(at 90% 70%, #fbcfe8 0%, transparent 50%), #fffbeb' } },
    { key: 'mesh-cool', label: 'Malla fría', style: { background: 'radial-gradient(at 20% 30%, #dbeafe 0%, transparent 50%), radial-gradient(at 80% 20%, #ede9fe 0%, transparent 50%), radial-gradient(at 50% 80%, #d1fae5 0%, transparent 50%), radial-gradient(at 90% 70%, #cffafe 0%, transparent 50%), #f8fafc' } },
];

const FONT_SIZES = [
    { key: 'small', label: 'Pequeño', size: '0.8rem' },
    { key: 'normal', label: 'Normal', size: '0.875rem' },
    { key: 'large', label: 'Grande', size: '1rem' },
];

// GIPHY API
const GIPHY_API_KEY = '1yM7Huuw4vzz42yGWyoafjDAtDQnkRcy';
const GIPHY_BASE_URL = 'https://api.giphy.com/v1/gifs';
const GIPHY_STICKERS_BASE_URL = 'https://api.giphy.com/v1/stickers';

// Detectar si un mensaje es un GIF de Tenor
const isGifMessage = (content) => content?.startsWith('[GIF]');
const isStickerMessage = (content) => content?.startsWith('[STICKER]');
const isBuzzMessage = (content) => content === '[BUZZ]';
const isTicketSuggestMessage = (content) => content?.startsWith('[TICKET_SUGGEST]');
const isTicketCreatedMessage = (content) => content?.startsWith('[TICKET_CREATED]');
const parseTicketData = (content) => {
    try { return JSON.parse(content.replace(/^\[TICKET_SUGGEST\]|\[TICKET_CREATED\]/, '')); }
    catch { return null; }
};
const getGifUrl = (content) => content?.replace('[GIF]', '');
const getStickerUrl = (content) => content?.replace('[STICKER]', '');
const isPdfFile = (mimeType, fileName) =>
    mimeType?.toLowerCase().includes('pdf') || fileName?.toLowerCase().endsWith('.pdf');

const getMessagePreview = (msg) => {
    if (!msg) return 'Sin mensajes';
    if (msg.is_deleted) return 'Mensaje eliminado';
    if (isBuzzMessage(msg.content)) return 'Zumbido';
    if (isCallMessage(msg.content)) {
        const data = (() => {
            try { return JSON.parse(msg.content.substring('[CALL]'.length)); } catch { return null; }
        })();
        if (!data) return 'Llamada';
        if (data.status === 'missed') return 'Llamada perdida';
        if (data.status === 'rejected') return 'Llamada rechazada';
        if (data.status === 'cancelled') return 'Llamada cancelada';
        return data.call_type === 'video' ? 'Videollamada' : 'Llamada';
    }
    if (isGifMessage(msg.content)) return 'GIF';
    if (isStickerMessage(msg.content)) return 'Sticker';
    if (isTicketSuggestMessage(msg.content)) return 'Sugerencia de ticket';
    if (isTicketCreatedMessage(msg.content)) return 'Ticket creado';
    if (msg.content) return msg.content;
    if (msg.file) {
        const mime = msg.file.mime_type || '';
        if (mime.startsWith('image/')) return 'Imagen';
        if (mime.startsWith('video/')) return 'Video';
        if (mime.startsWith('audio/')) return 'Audio';
        return 'Archivo';
    }
    if (msg.poll) return 'Encuesta';
    return 'Sin mensajes';
};

// Helper para obtener icono según tipo de archivo
const getFileIcon = (mimeType, fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();

    // PDF
    if (mimeType?.includes('pdf') || ext === 'pdf') {
        return <FileText size={20} className="file-icon pdf" />;
    }
    // Excel
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel') ||
        ['xls', 'xlsx', 'csv'].includes(ext)) {
        return <FileSpreadsheet size={20} className="file-icon excel" />;
    }
    // Word
    if (mimeType?.includes('word') || mimeType?.includes('document') ||
        ['doc', 'docx'].includes(ext)) {
        return <FileText size={20} className="file-icon word" />;
    }
    // PowerPoint
    if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint') ||
        ['ppt', 'pptx'].includes(ext)) {
        return <FileType size={20} className="file-icon ppt" />;
    }
    // Archivos comprimidos
    if (mimeType?.includes('zip') || mimeType?.includes('rar') ||
        ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
        return <FileArchive size={20} className="file-icon archive" />;
    }
    // Default
    return <File size={20} className="file-icon" />;
};

const ChatView = () => {
    const { user } = useAuth();
    const {
        conversations,
        activeConversation,
        messages,
        typingByConv,
        onlineUsers,
        loading,
        connected,
        hasMore,
        loadingMore,
        joinConversation,
        sendMessage,
        sendTyping,
        createConversation,
        createGroup,
        deleteConversation,
        uploadFile,
        deleteMessage,
        toggleReaction,
        togglePin,
        pinnedMessages,
        replyingTo,
        setReplyingTo,
        editingMessage,
        setEditingMessage,
        editMessage,
        forwardMessage,
        createPoll,
        votePoll,
        userPresence,
        setPresenceStatus,
        fetchAvailableUsers,
        fetchConversations,
        searchMessages,
        loadMoreMessages,
        inviteToGroup,
        removeFromGroup,
        leaveGroup,
        transferAdmin,
        sendBuzz,
        buzzActive,
        buzzCooldownRemaining,
        fetchBookmarks,
        addBookmark,
        removeBookmark,
        addBookmarkNote,
        removeBookmarkNote,
        uploadChatFile,
        createReminder
    } = useChat();
    const { startCall, status: callStatus } = useCall();

    const [messageInput, setMessageInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showNewChat, setShowNewChat] = useState(false);
    const [showCreateGroup, setShowCreateGroup] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [filePreviews, setFilePreviews] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [showMobileChat, setShowMobileChat] = useState(() =>
        sessionStorage.getItem(MOBILE_CHAT_STORAGE_KEY) === '1'
    );
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);
    const [showGroupSettings, setShowGroupSettings] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteSearch, setInviteSearch] = useState('');
    const [transferConfirm, setTransferConfirm] = useState(null);
    const [showMessageSearch, setShowMessageSearch] = useState(false);
    const [messageSearchQuery, setMessageSearchQuery] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [newChatSearch, setNewChatSearch] = useState('');
    const [groupChatSearch, setGroupChatSearch] = useState('');
    const [selectedMessageId, setSelectedMessageId] = useState(null);
    const [showReactionPicker, setShowReactionPicker] = useState(null); // message id
    const [showPinnedPanel, setShowPinnedPanel] = useState(false);
    const [showGifPicker, setShowGifPicker] = useState(false);
    const [gifSearch, setGifSearch] = useState('');
    const [gifs, setGifs] = useState([]);
    const [gifLoading, setGifLoading] = useState(false);
    const gifPickerRef = useRef(null);
    const gifSearchTimeoutRef = useRef(null);
    const [showStickerPicker, setShowStickerPicker] = useState(false);
    const [stickerSearch, setStickerSearch] = useState('');
    const [stickers, setStickers] = useState([]);
    const [stickerLoading, setStickerLoading] = useState(false);
    const stickerPickerRef = useRef(null);
    const stickerSearchTimeoutRef = useRef(null);
    const reactionPickerRef = useRef(null);
    const [viewerFile, setViewerFile] = useState(null); // { file, senderName, timestamp, index }
    const [forwardingMessage, setForwardingMessage] = useState(null); // msg to forward
    const [forwardSearch, setForwardSearch] = useState('');
    const [showPresenceMenu, setShowPresenceMenu] = useState(false);
    const [mentionQuery, setMentionQuery] = useState(null); // null = not mentioning, string = search query
    const [mentionIndex, setMentionIndex] = useState(0);
    const [showSummary, setShowSummary] = useState(null); // null or summary text
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [menuFlip, setMenuFlip] = useState(false);
    const [showPollCreator, setShowPollCreator] = useState(false);
    const [pollQuestion, setPollQuestion] = useState('');
    const [pollOptions, setPollOptions] = useState(['', '']);
    const [showBookmarks, setShowBookmarks] = useState(false);
    const [reminderMenuMsgId, setReminderMenuMsgId] = useState(null);
    const [bookmarks, setBookmarks] = useState([]);
    const [bookmarkedMessageIds, setBookmarkedMessageIds] = useState(new Set());
    const [noteInput, setNoteInput] = useState('');
    const [noteFile, setNoteFile] = useState(null);
    const [noteUploading, setNoteUploading] = useState(false);
    const noteTextareaRef = useRef(null);
    const noteFileInputRef = useRef(null);
    const [showCustomize, setShowCustomize] = useState(false);
    const [chatPrefs, setChatPrefs] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('chat_prefs')) || {};
        } catch { return {}; }
    });

    // Group creation state
    const [groupName, setGroupName] = useState('');
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [isDragging, setIsDragging] = useState(false);

    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const prevConversationRef = useRef(null);
    const chatViewRef = useRef(null);
    const fileInputRef = useRef(null);
    const dragCounterRef = useRef(0);
    const typingTimeoutRef = useRef(null);
    const textareaRef = useRef(null);
    const messageSearchInputRef = useRef(null);
    const searchDebounceRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const emojiButtonRef = useRef(null);
    const urlParamsProcessedRef = useRef(false);
    const instanceIdRef = useRef(Math.random().toString(36).slice(2, 8));
    const lastInteractionRef = useRef(null);

    const location = useLocation();

    // Ajustar contenedores padre para que el chat llene el espacio sin scrollbar externo
    useEffect(() => {
        const el = chatViewRef.current;
        if (!el) return;
        const parent = el.parentElement;
        const grandparent = parent?.parentElement;
        const savedStyles = {};

        if (parent) {
            savedStyles.parentHeight = parent.style.height;
            savedStyles.parentPB = parent.style.paddingBottom;
            savedStyles.parentMW = parent.style.maxWidth;
            parent.style.height = '100%';
            parent.style.paddingBottom = '0';
            parent.style.maxWidth = 'none';
        }
        if (grandparent) {
            savedStyles.gpOverflow = grandparent.style.overflow;
            grandparent.style.overflow = 'hidden';
        }

        return () => {
            if (parent) {
                parent.style.height = savedStyles.parentHeight || '';
                parent.style.paddingBottom = savedStyles.parentPB || '';
                parent.style.maxWidth = savedStyles.parentMW || '';
            }
            if (grandparent) {
                grandparent.style.overflow = savedStyles.gpOverflow || '';
            }
        };
    }, []);

    const API_URL = process.env.REACT_APP_API_URL || '/api';
    const BASE_URL = API_URL.replace('/api', ''); // Base URL for file downloads
    const authToken = localStorage.getItem('token');
    const [creatingTicketFromChat, setCreatingTicketFromChat] = useState(false);
    const [ticketPreview, setTicketPreview] = useState(null);
    const [createdTicketIds, setCreatedTicketIds] = useState(new Set());

    const setShowMobileChatWithReason = useCallback((nextValue, reason) => {
        setShowMobileChat(prev => {
            const next = typeof nextValue === 'function' ? nextValue(prev) : nextValue;
            const debugPayload = {
                reason,
                prev,
                next,
                instance: instanceIdRef.current,
                activeConversationId: activeConversation?.id ?? null,
                path: `${location.pathname}${location.search}`,
                ts: new Date().toISOString()
            };
            if (process.env.NODE_ENV !== 'production') {
                console.log('[ChatMobile]', debugPayload);
            }
            appendChatDebugTrail({ type: 'set_show_mobile_chat', ...debugPayload });
            return next;
        });
    }, [activeConversation?.id, location.pathname, location.search]);

    useEffect(() => {
        sessionStorage.setItem(MOBILE_CHAT_STORAGE_KEY, showMobileChat ? '1' : '0');
    }, [showMobileChat]);

    useEffect(() => {
        const instanceId = instanceIdRef.current;
        const navEntry = performance.getEntriesByType('navigation')[0];
        const recoveredTrail = (() => {
            try {
                return JSON.parse(sessionStorage.getItem(CHAT_DEBUG_TRAIL_KEY) || '[]');
            } catch {
                return [];
            }
        })();

        if (process.env.NODE_ENV !== 'production') {
            console.log('[ChatMobile] mount', {
                instance: instanceId,
                initialShowMobileChat: showMobileChat,
                navigationType: navEntry?.type || 'unknown',
                recoveredTrail
            });
        }
        appendChatDebugTrail({
            type: 'mount',
            instance: instanceId,
            initialShowMobileChat: showMobileChat,
            navigationType: navEntry?.type || 'unknown',
            path: `${window.location.pathname}${window.location.search}`,
            ts: new Date().toISOString()
        });

        const onBeforeUnload = () => {
            appendChatDebugTrail({
                type: 'beforeunload',
                instance: instanceId,
                path: `${window.location.pathname}${window.location.search}`,
                lastInteraction: lastInteractionRef.current,
                ts: new Date().toISOString()
            });
        };
        const onPageHide = () => {
            appendChatDebugTrail({
                type: 'pagehide',
                instance: instanceId,
                path: `${window.location.pathname}${window.location.search}`,
                lastInteraction: lastInteractionRef.current,
                ts: new Date().toISOString()
            });
        };
        const onClickCapture = (e) => {
            const t = e.target;
            if (!(t instanceof Element)) return;
            lastInteractionRef.current = {
                kind: 'click',
                tag: t.tagName,
                id: t.id || null,
                classes: t.className || null,
                text: (t.textContent || '').trim().slice(0, 80),
                ts: new Date().toISOString()
            };
        };
        const onKeyDownCapture = (e) => {
            lastInteractionRef.current = {
                kind: 'keydown',
                key: e.key,
                ctrl: !!e.ctrlKey,
                meta: !!e.metaKey,
                shift: !!e.shiftKey,
                alt: !!e.altKey,
                ts: new Date().toISOString()
            };
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        window.addEventListener('pagehide', onPageHide);
        document.addEventListener('click', onClickCapture, true);
        document.addEventListener('keydown', onKeyDownCapture, true);

        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            window.removeEventListener('pagehide', onPageHide);
            document.removeEventListener('click', onClickCapture, true);
            document.removeEventListener('keydown', onKeyDownCapture, true);
            appendChatDebugTrail({
                type: 'unmount',
                instance: instanceId,
                path: `${window.location.pathname}${window.location.search}`,
                ts: new Date().toISOString()
            });
            if (process.env.NODE_ENV !== 'production') {
                console.log('[ChatMobile] unmount', { instance: instanceId });
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Persistir última conversación activa para restaurarla tras recarga
    useEffect(() => {
        if (activeConversation?.id) {
            sessionStorage.setItem(LAST_CONVERSATION_STORAGE_KEY, String(activeConversation.id));
        }
    }, [activeConversation?.id]);

    // Restaurar conversación activa automáticamente después de un reload
    useEffect(() => {
        if (!connected) return;
        if (activeConversation?.id) return;
        if (!conversations.length) return;

        const fromUrl = new URLSearchParams(location.search).get('conversation');
        if (fromUrl) return;

        const storedIdRaw = sessionStorage.getItem(LAST_CONVERSATION_STORAGE_KEY);
        const storedId = Number(storedIdRaw);
        if (!storedId) return;

        const exists = conversations.some(c => c.id === storedId);
        if (!exists) return;

        joinConversation(storedId);
        setShowMobileChatWithReason(true, 'restore_last_conversation_after_reload');
    }, [connected, activeConversation?.id, conversations, location.search, joinConversation, setShowMobileChatWithReason]);

    // Defensa: evitar submits implícitos que provoquen reload en /chat
    useEffect(() => {
        const onSubmitCapture = (e) => {
            const target = e.target;
            if (!(target instanceof HTMLFormElement)) return;
            if (!target.closest('.chat-view')) return;

            e.preventDefault();
            e.stopPropagation();

            const entry = {
                type: 'prevented_submit',
                instance: instanceIdRef.current,
                formId: target.id || null,
                action: target.getAttribute('action') || null,
                ts: new Date().toISOString()
            };
            appendChatDebugTrail(entry);
            if (process.env.NODE_ENV !== 'production') {
                console.warn('[ChatMobile] prevented submit inside chat-view', entry);
            }
        };

        document.addEventListener('submit', onSubmitCapture, true);
        return () => document.removeEventListener('submit', onSubmitCapture, true);
    }, []);

    // Cerrar menú de acciones al hacer click afuera
    useEffect(() => {
        if (selectedMessageId === null) return;
        const handleClickOutside = (e) => {
            // No cerrar si el click fue dentro del menú de acciones
            if (e.target.closest('.message-actions')) return;
            setSelectedMessageId(null);
            setShowReactionPicker(null);
            setReminderMenuMsgId(null);
        };
        // Delay para que no se cierre inmediatamente al abrirse
        const timer = setTimeout(() => {
            document.addEventListener('click', handleClickOutside);
        }, 50);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [selectedMessageId]);

    // Scroll al último mensaje
    const forceScrollBottom = useCallback(() => {
        const container = messagesContainerRef.current;
        if (container) container.scrollTop = container.scrollHeight;
    }, []);

    const scrollToBottomSmooth = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Al cambiar de conversación: scroll instantáneo agresivo al fondo
    useEffect(() => {
        if (!activeConversation?.id || !messages.length) return;
        if (activeConversation.id === prevConversationRef.current) return;
        prevConversationRef.current = activeConversation.id;

        // Scroll inmediato + repetido para cubrir contenido que carga progresivamente
        forceScrollBottom();
        const intervals = [0, 50, 150, 400, 800];
        const timers = intervals.map(ms => setTimeout(forceScrollBottom, ms));

        // Observer que fuerza scroll al fondo cada vez que el contenido cambia (imágenes cargando, etc.)
        const container = messagesContainerRef.current;
        let observer;
        if (container) {
            observer = new MutationObserver(forceScrollBottom);
            observer.observe(container, { childList: true, subtree: true, attributes: true });
            // Desactivar observer después de 2s para no interferir con scroll manual del usuario
            setTimeout(() => observer?.disconnect(), 2000);
        }

        return () => {
            timers.forEach(clearTimeout);
            observer?.disconnect();
        };
    }, [activeConversation?.id, messages.length, forceScrollBottom]);

    // Al llegar mensajes nuevos en la misma conversación: smooth scroll solo si estamos cerca del fondo
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!container || !messages.length) return;
        if (activeConversation?.id !== prevConversationRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = container;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 150;
        if (isNearBottom) {
            scrollToBottomSmooth();
        }
    }, [messages, activeConversation?.id, scrollToBottomSmooth]);

    // Handle URL params (?conversation=ID&draft=message)
    useEffect(() => {
        if (urlParamsProcessedRef.current) return;
        if (!connected) return;
        const params = new URLSearchParams(location.search);
        const conversationId = params.get('conversation');
        const draft = params.get('draft');

        if (conversationId) {
            urlParamsProcessedRef.current = true;
            const convId = parseInt(conversationId);
            joinConversation(convId);
            setShowMobileChatWithReason(true, 'url_params_open_conversation');

            if (draft) {
                setMessageInput(decodeURIComponent(draft));
                setTimeout(() => {
                    textareaRef.current?.focus();
                }, 500);
            }
        }
    }, [location.search, connected, joinConversation, setShowMobileChatWithReason]);

    // Cargar bookmarks al inicio
    useEffect(() => {
        fetchBookmarks().then(bms => {
            setBookmarks(bms);
            setBookmarkedMessageIds(new Set(bms.filter(b => !b.is_note).map(b => b.id)));
        });
    }, [fetchBookmarks]);

    const handleAddNote = useCallback(async () => {
        const trimmed = noteInput.trim();
        if (!trimmed && !noteFile) return;
        setNoteUploading(true);
        try {
            let fileData = null;
            if (noteFile) {
                fileData = await uploadChatFile(noteFile);
                if (!fileData) return;
            }
            const result = await addBookmarkNote({
                content: trimmed || null,
                file_id: fileData?.id || null,
            });
            if (result) {
                const newNote = {
                    id: `note-${result.bookmark_id}`,
                    is_note: true,
                    bookmark_id: result.bookmark_id,
                    content: result.content,
                    file: result.file,
                    sender_name: 'Tú',
                    sender_id: user?.id,
                    bookmarked_at: result.created_at,
                    created_at: result.created_at,
                };
                setBookmarks(prev => [newNote, ...prev]);
                setNoteInput('');
                setNoteFile(null);
                if (noteTextareaRef.current) {
                    noteTextareaRef.current.style.height = 'auto';
                }
            }
        } finally {
            setNoteUploading(false);
        }
    }, [noteInput, noteFile, addBookmarkNote, uploadChatFile, user?.id]);

    const adjustNoteTextareaHeight = useCallback(() => {
        const ta = noteTextareaRef.current;
        if (!ta) return;
        ta.style.height = 'auto';
        ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
    }, []);

    const handleNotePaste = useCallback((e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (const item of items) {
            if (item.kind === 'file') {
                const f = item.getAsFile();
                if (f) {
                    setNoteFile(f);
                    e.preventDefault();
                    return;
                }
            }
        }
    }, []);

    const handleRemoveNote = useCallback(async (bookmarkId) => {
        const ok = await removeBookmarkNote(bookmarkId);
        if (ok) {
            setBookmarks(prev => prev.filter(b => b.bookmark_id !== bookmarkId));
        }
    }, [removeBookmarkNote]);

    const handleToggleBookmark = useCallback(async (messageId) => {
        if (bookmarkedMessageIds.has(messageId)) {
            const ok = await removeBookmark(messageId);
            if (ok) {
                setBookmarkedMessageIds(prev => { const next = new Set(prev); next.delete(messageId); return next; });
                setBookmarks(prev => prev.filter(b => b.id !== messageId));
            }
        } else {
            const result = await addBookmark(messageId);
            if (result && !result.already_exists) {
                setBookmarkedMessageIds(prev => new Set([...prev, messageId]));
                // Recargar bookmarks para tener data completa
                fetchBookmarks().then(bms => {
                    setBookmarks(bms);
                    setBookmarkedMessageIds(new Set(bms.map(b => b.id)));
                });
            }
        }
    }, [bookmarkedMessageIds, addBookmark, removeBookmark, fetchBookmarks]);

    const handleSetReminder = useCallback(async (messageId, option) => {
        const now = new Date();
        let remindAt;
        switch (option) {
            case '30min':
                remindAt = new Date(now.getTime() + 30 * 60000);
                break;
            case '1h':
                remindAt = new Date(now.getTime() + 60 * 60000);
                break;
            case '3h':
                remindAt = new Date(now.getTime() + 3 * 60 * 60000);
                break;
            case 'tomorrow':
                remindAt = new Date(now);
                remindAt.setDate(remindAt.getDate() + 1);
                remindAt.setHours(9, 0, 0, 0);
                break;
            default:
                return;
        }
        await createReminder(messageId, remindAt.toISOString());
        setReminderMenuMsgId(null);
        setSelectedMessageId(null);
    }, [createReminder]);

    // Listener para recordatorios que llegan via socket
    useEffect(() => {
        const handleReminderEvent = (e) => {
            const { message, conversation_id } = e.detail;
            if (!message) return;
            // Mostrar toast de recordatorio
            const toast = document.createElement('div');
            toast.className = 'reminder-toast';
            toast.innerHTML = `
                <div class="reminder-toast-icon"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg></div>
                <div class="reminder-toast-body">
                    <div class="reminder-toast-title">Recordatorio</div>
                    <div class="reminder-toast-sender">${message.sender_name || ''}</div>
                    <div class="reminder-toast-text">${message.content ? message.content.substring(0, 100) : 'Archivo'}</div>
                </div>
            `;
            toast.addEventListener('click', () => {
                if (conversation_id) joinConversation(conversation_id);
                toast.remove();
            });
            document.body.appendChild(toast);
            requestAnimationFrame(() => toast.classList.add('visible'));
            setTimeout(() => {
                toast.classList.remove('visible');
                setTimeout(() => toast.remove(), 300);
            }, 8000);
        };
        window.addEventListener('chat_reminder', handleReminderEvent);
        return () => window.removeEventListener('chat_reminder', handleReminderEvent);
    }, [joinConversation]);

    // Cargar usuarios disponibles para nuevo chat o grupo
    useEffect(() => {
        if (showNewChat || showCreateGroup || showInviteModal) {
            fetchAvailableUsers().then(setAvailableUsers);
        }
    }, [showNewChat, showCreateGroup, showInviteModal, fetchAvailableUsers]);

    // Cerrar emoji picker al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                showEmojiPicker &&
                emojiPickerRef.current &&
                !emojiPickerRef.current.contains(e.target) &&
                emojiButtonRef.current &&
                !emojiButtonRef.current.contains(e.target)
            ) {
                setShowEmojiPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showEmojiPicker]);

    // Insertar emoji en la posición del cursor
    const handleEmojiClick = useCallback((emojiData) => {
        const textarea = textareaRef.current;
        const start = textarea?.selectionStart ?? messageInput.length;
        const end = textarea?.selectionEnd ?? messageInput.length;
        const newValue = messageInput.slice(0, start) + emojiData.emoji + messageInput.slice(end);
        setMessageInput(newValue);

        const newCursorPos = start + emojiData.emoji.length;
        requestAnimationFrame(() => {
            if (textarea) {
                textarea.focus();
                textarea.selectionStart = newCursorPos;
                textarea.selectionEnd = newCursorPos;
            }
        });
    }, [messageInput]);

    // Manejar typing indicator
    const handleTyping = useCallback(() => {
        sendTyping(true);

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        typingTimeoutRef.current = setTimeout(() => {
            sendTyping(false);
        }, 2000);
    }, [sendTyping]);

    // Enviar mensaje
    const handleSendMessage = async () => {
        if (uploading) return;
        if (!messageInput.trim() && selectedFiles.length === 0) return;

        // Validar longitud del mensaje
        if (messageInput.length > MAX_MESSAGE_LENGTH) {
            alert(`El mensaje excede el límite de ${MAX_MESSAGE_LENGTH} caracteres`);
            return;
        }

        // Si estamos editando, enviar edición en vez de nuevo mensaje
        if (editingMessage) {
            editMessage(editingMessage.id, messageInput);
            setMessageInput('');
            setEditingMessage(null);
            sendTyping(false);
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
            return;
        }

        const conversationId = activeConversation?.id;
        const replyId = replyingTo?.id || null;

        try {
            if (selectedFiles.length > 0) {
                setUploading(true);
                // Subir cada archivo y enviar como mensaje separado
                for (let i = 0; i < selectedFiles.length; i++) {
                    const result = await uploadFile(selectedFiles[i]);
                    if (result) {
                        // Primer archivo lleva el texto del mensaje, los demás solo el archivo
                        if (i === 0 && messageInput.trim()) {
                            sendMessage(messageInput, result.id, replyId);
                        } else {
                            sendMessage(null, result.id, i === 0 ? replyId : null);
                        }
                    }
                }
                setSelectedFiles([]);
                setFilePreviews([]);
            } else {
                sendMessage(messageInput, null, replyId);
            }

            setMessageInput('');
            setReplyingTo(null);
            setShowEmojiPicker(false);
            sendTyping(false);
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
        } finally {
            setUploading(false);
            // En móvil, mantener la vista de conversación activa después de subir archivos
            if (conversationId) {
                setShowMobileChatWithReason(true, 'send_message_completed');
            }
        }
    };

    // Buscar GIFs en GIPHY
    const fetchGifs = useCallback(async (query) => {
        setGifLoading(true);
        try {
            const endpoint = query ? 'search' : 'trending';
            const params = new URLSearchParams({
                api_key: GIPHY_API_KEY,
                limit: '20',
                rating: 'g',
                lang: 'es'
            });
            if (query) params.set('q', query);
            const response = await fetch(`${GIPHY_BASE_URL}/${endpoint}?${params}`);
            if (response.ok) {
                const data = await response.json();
                setGifs(data.data || []);
            }
        } catch (err) {
            console.error('Error fetching GIFs:', err);
        }
        setGifLoading(false);
    }, []);

    // Cargar trending al abrir el picker
    useEffect(() => {
        if (showGifPicker && gifs.length === 0) {
            fetchGifs('');
        }
    }, [showGifPicker, fetchGifs, gifs.length]);

    // Búsqueda con debounce
    const handleGifSearch = (value) => {
        setGifSearch(value);
        clearTimeout(gifSearchTimeoutRef.current);
        gifSearchTimeoutRef.current = setTimeout(() => {
            fetchGifs(value);
        }, 400);
    };

    // Buscar stickers en GIPHY
    const fetchStickers = useCallback(async (query) => {
        setStickerLoading(true);
        try {
            const endpoint = query ? 'search' : 'trending';
            const params = new URLSearchParams({
                api_key: GIPHY_API_KEY,
                limit: '24',
                rating: 'g',
                lang: 'es'
            });
            if (query) params.set('q', query);
            const response = await fetch(`${GIPHY_STICKERS_BASE_URL}/${endpoint}?${params}`);
            if (response.ok) {
                const data = await response.json();
                setStickers(data.data || []);
            }
        } catch (err) {
            console.error('Error fetching stickers:', err);
        }
        setStickerLoading(false);
    }, []);

    useEffect(() => {
        if (showStickerPicker && stickers.length === 0) {
            fetchStickers('');
        }
    }, [showStickerPicker, fetchStickers, stickers.length]);

    const handleStickerSearch = (value) => {
        setStickerSearch(value);
        clearTimeout(stickerSearchTimeoutRef.current);
        stickerSearchTimeoutRef.current = setTimeout(() => {
            fetchStickers(value);
        }, 300);
    };

    const handleSendSticker = (sticker) => {
        const url = sticker.images?.original?.url || sticker.images?.fixed_height?.url;
        if (url) {
            sendMessage(`[STICKER]${url}`);
            setShowStickerPicker(false);
            setStickerSearch('');
            setStickers([]);
        }
    };

    // Enviar GIF
    const handleSendGif = (gif) => {
        const gifUrl = gif.images?.original?.url || gif.images?.fixed_height?.url;
        if (gifUrl) {
            sendMessage(`[GIF]${gifUrl}`);
            setShowGifPicker(false);
            setGifSearch('');
            setGifs([]);
        }
    };

    // Chat → Ticket: TI analiza con IA y obtiene un preview para aceptar/cancelar antes de crear.
    const handleSuggestTicket = async () => {
        if (!activeConversation || creatingTicketFromChat) return;
        setCreatingTicketFromChat(true);
        try {
            const res = await fetch(`${API_URL}/tickets/ai/from-chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                body: JSON.stringify({ conversation_id: activeConversation.id }),
            });
            if (!res.ok) throw new Error('Error al analizar');
            const data = await res.json();
            setTicketPreview(data);
        } catch (err) {
            console.error('Error analizando ticket:', err);
        }
        setCreatingTicketFromChat(false);
    };

    // TI confirma el preview → crear ticket y notificar en el chat
    const handleConfirmTicketPreview = async () => {
        if (!ticketPreview) return;
        setCreatingTicketFromChat(true);
        try {
            const res = await fetch(`${API_URL}/tickets/from-chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                body: JSON.stringify(ticketPreview),
            });
            if (!res.ok) throw new Error('Error al crear ticket');
            const ticket = await res.json();
            sendMessage(`[TICKET_CREATED]${JSON.stringify({ id: ticket.id, titulo: ticket.titulo })}`);
            setTicketPreview(null);
        } catch (err) {
            console.error('Error creando ticket:', err);
        }
        setCreatingTicketFromChat(false);
    };

    // Crear ticket desde sugerencia de chat
    const handleCreateTicketFromChat = async (ticketData, msgId) => {
        try {
            const res = await fetch(`${API_URL}/tickets/from-chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                body: JSON.stringify(ticketData),
            });
            if (!res.ok) throw new Error('Error al crear ticket');
            const ticket = await res.json();
            setCreatedTicketIds(prev => new Set(prev).add(msgId));
            sendMessage(`[TICKET_CREATED]${JSON.stringify({ id: ticket.id, titulo: ticket.titulo })}`);
        } catch (err) {
            console.error('Error creando ticket desde chat:', err);
        }
    };

    // Cerrar GIF picker al clickear fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showGifPicker && gifPickerRef.current && !gifPickerRef.current.contains(e.target)) {
                setShowGifPicker(false);
            }
            if (showStickerPicker && stickerPickerRef.current && !stickerPickerRef.current.contains(e.target)) {
                setShowStickerPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showGifPicker, showStickerPicker]);

    // Manejar tecla Enter
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Auto-resize textarea
    const adjustTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
        }
    };

    // Pegar archivo desde el portapapeles (Ctrl+V / Cmd+V)
    const handlePaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        const newFiles = [];

        for (const item of items) {
            if (item.kind === 'file') {
                e.preventDefault();
                const file = item.getAsFile();
                if (!file) continue;

                // Validar tamaño
                if (file.size > MAX_FILE_SIZE) {
                    alert('El archivo excede el tamaño máximo permitido (20MB)');
                    continue;
                }

                // Para archivos pegados sin nombre real, generar uno
                let fileName = file.name;
                if (!fileName || fileName === 'image.png') {
                    const ext = file.type.split('/')[1] || 'png';
                    fileName = `imagen_pegada_${Date.now()}_${newFiles.length}.${ext}`;
                }

                // Validar extensión
                const ext = fileName.split('.').pop()?.toLowerCase();
                if (!ALLOWED_EXTENSIONS.includes(ext)) {
                    alert(`Tipo de archivo no permitido. Extensiones permitidas: ${ALLOWED_EXTENSIONS.join(', ')}`);
                    continue;
                }

                const namedFile = new window.File([file], fileName, { type: file.type });
                newFiles.push(namedFile);

                if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    const idx = newFiles.length - 1;
                    reader.onload = (ev) => {
                        setFilePreviews(prev => {
                            const updated = [...prev];
                            updated[selectedFiles.length + idx] = ev.target.result;
                            return updated;
                        });
                    };
                    reader.readAsDataURL(file);
                }
            }
        }

        if (newFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...newFiles]);
        }
    };

    // Seleccionar archivos (múltiples)
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const validFiles = [];
        for (const file of files) {
            if (file.size > MAX_FILE_SIZE) {
                alert(`${file.name} excede el tamaño máximo permitido (20MB)`);
                continue;
            }
            const ext = file.name.split('.').pop()?.toLowerCase();
            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                alert(`${file.name}: tipo no permitido. Extensiones permitidas: ${ALLOWED_EXTENSIONS.join(', ')}`);
                continue;
            }
            validFiles.push(file);
        }

        if (validFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...validFiles]);

            // Generar previews para imágenes
            validFiles.forEach((file, i) => {
                if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                        setFilePreviews(prev => {
                            const updated = [...prev];
                            updated[selectedFiles.length + i] = ev.target.result;
                            return updated;
                        });
                    };
                    reader.readAsDataURL(file);
                }
            });
        }
        e.target.value = '';
    };

    // Drag & drop de archivos al área de chat
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current++;
        if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current--;
        if (dragCounterRef.current === 0) setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files || []);
        if (files.length === 0) return;

        const validFiles = [];
        for (const file of files) {
            if (file.size > MAX_FILE_SIZE) {
                alert(`${file.name} excede el tamaño máximo permitido (20MB)`);
                continue;
            }
            const ext = file.name.split('.').pop()?.toLowerCase();
            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                alert(`${file.name}: tipo no permitido. Extensiones permitidas: ${ALLOWED_EXTENSIONS.join(', ')}`);
                continue;
            }
            validFiles.push(file);
        }

        if (validFiles.length > 0) {
            setSelectedFiles(prev => {
                const base = prev.length;
                validFiles.forEach((file, i) => {
                    if (file.type.startsWith('image/')) {
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                            setFilePreviews(p => {
                                const updated = [...p];
                                updated[base + i] = ev.target.result;
                                return updated;
                            });
                        };
                        reader.readAsDataURL(file);
                    }
                });
                return [...prev, ...validFiles];
            });
        }
    };

    // Guardar preferencias de chat
    const updateChatPrefs = useCallback((key, value) => {
        setChatPrefs(prev => {
            const updated = { ...prev, [key]: value };
            localStorage.setItem('chat_prefs', JSON.stringify(updated));
            return updated;
        });
    }, []);

    // Obtener resumen con IA
    const [summaryPeriod, setSummaryPeriod] = useState('24h');
    const [summaryMeta, setSummaryMeta] = useState(null);

    const handleGetSummary = async (period) => {
        if (!activeConversation || summaryLoading) return;
        const p = period || summaryPeriod;
        setSummaryPeriod(p);
        setSummaryLoading(true);
        setShowSummary(null);
        setSummaryMeta(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/chat/conversations/${activeConversation.id}/summary?period=${p}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setShowSummary(data.summary);
                setSummaryMeta({ count: data.message_count, period: data.period });
            } else {
                const err = await response.json();
                setShowSummary(err.error || 'No se pudo generar el resumen');
            }
        } catch {
            setShowSummary('Error de conexión al generar resumen');
        } finally {
            setSummaryLoading(false);
        }
    };

    // Renderizar contenido de mensaje con links clickeables y menciones resaltadas
    // Detecta si el contenido tiene sintaxis markdown (para no renderizar markdown en mensajes planos)
    const hasMarkdownSyntax = useCallback((text) => {
        if (!text) return false;
        return /(\*\*.+?\*\*|__.+?__|_.+?_|\*.+?\*|~~.+?~~|`.+?`|```[\s\S]*?```|^#{1,6}\s|^>\s|^[-*+]\s|^\d+\.\s|^\|.+\|)/m.test(text);
    }, []);

    const renderMessageContent = useCallback((content) => {
        if (!content) return content;

        const mentionRegex = /(@\w[\w\s]*?\w(?=\s|$|@))/g;
        const taskRegex = /(#[A-Z]{2,6}-\d+)/g;

        // Helper que parsea menciones de tarea en un texto plano
        const parseTaskMentions = (text, keyPrefix) => {
            if (!text || typeof text !== 'string') return text;
            const parts = text.split(taskRegex);
            if (parts.length === 1) return text;
            return parts.map((part, i) => {
                if (i % 2 === 1) {
                    // Codigo de tarea (#SLUG-N)
                    return <TaskMentionChip key={`${keyPrefix}-tm-${i}`} codigo={part.substring(1)} />;
                }
                return part;
            });
        };

        // Si no tiene markdown, usar render simple (más ligero)
        if (!hasMarkdownSyntax(content)) {
            const urlRegex = /(https?:\/\/[^\s<>"')\]]+)/gi;
            const urlParts = content.split(urlRegex);
            if (urlParts.length === 1 && !content.match(mentionRegex) && !content.match(taskRegex)) {
                return content;
            }

            return urlParts.map((part, i) => {
                if (urlRegex.test(part)) {
                    urlRegex.lastIndex = 0;
                    return <a key={`url-${i}`} href={part} target="_blank" rel="noopener noreferrer" className="message-link" onClick={e => e.stopPropagation()}>{part}</a>;
                }
                // Primero menciones de tarea, despues @mentions
                const mentionParts = part.split(mentionRegex);
                if (mentionParts.length === 1) {
                    return <React.Fragment key={`p-${i}`}>{parseTaskMentions(part, `p-${i}`)}</React.Fragment>;
                }
                return mentionParts.map((mp, j) => {
                    if (mp.startsWith('@')) {
                        const name = mp.substring(1);
                        const isCurrentUser = user?.empleado?.nombre_display && name.toLowerCase() === user.empleado.nombre_display.toLowerCase();
                        return <span key={`m-${i}-${j}`} className={`mention-highlight ${isCurrentUser ? 'mention-me' : ''}`}>{mp}</span>;
                    }
                    return <React.Fragment key={`m-${i}-${j}`}>{parseTaskMentions(mp, `m-${i}-${j}`)}</React.Fragment>;
                });
            });
        }

        // Render con Markdown completo
        const renderMentions = (text) => {
            if (!text || typeof text !== 'string') return text;
            const parts = text.split(mentionRegex);
            if (parts.length === 1) return text;
            return parts.map((part, i) => {
                if (part.startsWith('@')) {
                    const name = part.substring(1);
                    const isCurrentUser = user?.empleado?.nombre_display && name.toLowerCase() === user.empleado.nombre_display.toLowerCase();
                    return <span key={`m-${i}`} className={`mention-highlight ${isCurrentUser ? 'mention-me' : ''}`}>{part}</span>;
                }
                return part;
            });
        };

        return (
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    p: ({ children }) => <span className="md-p">{children}</span>,
                    a: ({ href, children }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="message-link" onClick={e => e.stopPropagation()}>{children}</a>
                    ),
                    code: ({ inline, className, children }) => {
                        if (inline) {
                            return <code className="md-inline-code">{children}</code>;
                        }
                        const lang = className?.replace('language-', '') || '';
                        return (
                            <pre className="md-code-block" data-lang={lang || undefined}>
                                <code>{children}</code>
                            </pre>
                        );
                    },
                    blockquote: ({ children }) => <blockquote className="md-blockquote">{children}</blockquote>,
                    table: ({ children }) => <table className="md-table">{children}</table>,
                    th: ({ children }) => <th className="md-th">{children}</th>,
                    td: ({ children }) => <td className="md-td">{children}</td>,
                    ul: ({ children }) => <ul className="md-list">{children}</ul>,
                    ol: ({ children }) => <ol className="md-list md-list-ordered">{children}</ol>,
                    li: ({ children }) => <li className="md-li">{children}</li>,
                    h1: ({ children }) => <strong className="md-heading md-h1">{children}</strong>,
                    h2: ({ children }) => <strong className="md-heading md-h2">{children}</strong>,
                    h3: ({ children }) => <strong className="md-heading md-h3">{children}</strong>,
                    h4: ({ children }) => <strong className="md-heading">{children}</strong>,
                    h5: ({ children }) => <strong className="md-heading">{children}</strong>,
                    h6: ({ children }) => <strong className="md-heading">{children}</strong>,
                    hr: () => <hr className="md-hr" />,
                    strong: ({ children }) => <strong>{children}</strong>,
                    em: ({ children }) => <em>{children}</em>,
                    del: ({ children }) => <del>{children}</del>,
                    text: ({ children }) => <>{renderMentions(children)}</>,
                }}
            >
                {content}
            </ReactMarkdown>
        );
    }, [user, hasMarkdownSyntax]);

    // Insertar mención en el textarea
    const insertMention = useCallback((participant) => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        const cursorPos = textarea.selectionStart;
        const textBefore = messageInput.substring(0, cursorPos);
        const textAfter = messageInput.substring(cursorPos);
        const atIndex = textBefore.lastIndexOf('@');
        const newText = textBefore.substring(0, atIndex) + `@${participant.nombre} ` + textAfter;
        setMessageInput(newText);
        setMentionQuery(null);
        setTimeout(() => {
            const newPos = atIndex + participant.nombre.length + 2;
            textarea.setSelectionRange(newPos, newPos);
            textarea.focus();
        }, 0);
    }, [messageInput]);

    // Iniciar nueva conversación
    const handleStartConversation = async (userId) => {
        const conv = await createConversation([userId]);
        if (conv) {
            joinConversation(conv.id);
            setShowNewChat(false);
            setShowMobileChatWithReason(true, 'start_conversation');
        }
    };

    // Seleccionar conversación
    const handleSelectConversation = (conv) => {
        joinConversation(conv.id);
        setShowBookmarks(false);
        setShowMobileChatWithReason(true, 'select_conversation');
        setShowOptionsMenu(false);
        setShowGroupSettings(false);
    };

    // Crear grupo
    const handleCreateGroup = async () => {
        if (!groupName.trim()) {
            alert('El nombre del grupo es requerido');
            return;
        }
        if (selectedMembers.length === 0) {
            alert('Selecciona al menos un miembro');
            return;
        }

        const conv = await createGroup(groupName, selectedMembers);
        if (conv) {
            joinConversation(conv.id);
            setShowCreateGroup(false);
            setGroupName('');
            setSelectedMembers([]);
            setShowMobileChatWithReason(true, 'create_group');
        }
    };

    // Toggle selección de miembro para grupo
    const toggleMemberSelection = (userId) => {
        setSelectedMembers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    // Invitar usuarios al grupo
    const handleInviteToGroup = async (userIds) => {
        if (!activeConversation?.is_group) return;

        const result = await inviteToGroup(activeConversation.id, userIds);
        if (result.error) {
            alert(result.error);
        } else {
            setShowInviteModal(false);
            setInviteSearch('');
        }
    };

    // Transferir admin del grupo
    const handleTransferAdmin = async (userId) => {
        if (!activeConversation?.is_group) return;

        const result = await transferAdmin(activeConversation.id, userId);
        if (result.error) {
            alert(result.error);
        } else {
            setTransferConfirm(null);
            setShowGroupSettings(false);
        }
    };

    // Remover usuario del grupo
    const handleRemoveFromGroup = async (userId) => {
        if (!activeConversation?.is_group) return;

        if (window.confirm('¿Estás seguro de remover a este usuario del grupo?')) {
            const result = await removeFromGroup(activeConversation.id, userId);
            if (result.error) {
                alert(result.error);
            }
        }
    };

    // Salir del grupo
    const handleLeaveGroup = async () => {
        if (!activeConversation?.is_group) return;

        if (window.confirm('¿Estás seguro de salir de este grupo?')) {
            const result = await leaveGroup(activeConversation.id);
            if (result.error) {
                alert(result.error);
            } else {
                setShowOptionsMenu(false);
                setShowMobileChatWithReason(false, 'leave_group');
            }
        }
    };

    // Búsqueda de mensajes
    const handleMessageSearch = useCallback((query) => {
        setMessageSearchQuery(query);
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            if (query.trim()) {
                searchMessages(query.trim());
            } else {
                searchMessages('');
            }
        }, 300);
    }, [searchMessages]);

    const closeMessageSearch = useCallback(() => {
        setShowMessageSearch(false);
        setMessageSearchQuery('');
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchMessages('');
    }, [searchMessages]);

    // Highlight helper: wraps matching text in <mark> tags
    const highlightText = useCallback((text, query) => {
        if (!query || !text) return text;
        const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === query.toLowerCase()
                ? <mark key={i}>{part}</mark>
                : part
        );
    }, []);

    // Eliminar conversación
    const handleDeleteConversation = async () => {
        if (activeConversation && window.confirm('¿Estás seguro de eliminar esta conversación?')) {
            await deleteConversation(activeConversation.id);
            setShowOptionsMenu(false);
            setShowMobileChatWithReason(false, 'delete_conversation');
        }
    };

    // Filtrar conversaciones y separar grupos de chats individuales
    const filteredConversations = conversations.filter(conv =>
        incluyeTexto(conv.name, searchQuery)
    );
    const groupConversations = filteredConversations.filter(conv => conv.is_group);
    const directConversations = filteredConversations.filter(conv => !conv.is_group);

    // Parsear timestamp UTC del backend (sin 'Z') correctamente
    const parseDate = (dateString) => {
        if (!dateString) return new Date(NaN);
        return new Date(dateString);
    };

    // Formatear hora
    const formatTime = (dateString) => {
        const date = parseDate(dateString);
        return date.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' });
    };

    // Formatear fecha
    const formatDate = (dateString) => {
        const date = parseDate(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return 'Hoy';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Ayer';
        }
        return date.toLocaleDateString('es-VE', { day: '2-digit', month: 'short' });
    };

    // Formatear tiempo para sidebar: hoy muestra hora, ayer "Ayer", más antiguo muestra fecha
    const formatSidebarTime = (dateString) => {
        const date = parseDate(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return date.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' });
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Ayer';
        }
        return date.toLocaleDateString('es-VE', { day: '2-digit', month: 'short' });
    };

    // Agrupar mensajes por día (memoizado)
    const messageGroups = useMemo(() => {
        const groups = {};
        messages.forEach(msg => {
            const date = parseDate(msg.created_at).toDateString();
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(msg);
        });
        return groups;
    }, [messages]);

    // Cargar mensajes anteriores preservando posición de scroll
    const handleLoadMore = useCallback(async () => {
        const container = messagesContainerRef.current;
        if (!container) return;
        const prevScrollHeight = container.scrollHeight;
        await loadMoreMessages();
        requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight - prevScrollHeight;
        });
    }, [loadMoreMessages]);

    // Recopilar archivos de la conversación para el visor
    const conversationFiles = useMemo(() =>
        messages.filter(m => m.file && !m.is_deleted).map(m => ({
            file: m.file,
            senderName: m.sender_name,
            timestamp: m.created_at
        })),
        [messages]
    );

    // Total de mensajes para determinar cuáles animar
    const totalMessages = messages.length;

    return (
        <div className={`chat-view bubble-shape-${chatPrefs.bubbleShape || 'tail'}`} ref={chatViewRef} style={(() => {
            const bub = BUBBLE_COLORS.find(c => c.key === chatPrefs.bubbleColor) || BUBBLE_COLORS.find(c => c.key === 'default');
            const inc = BUBBLE_COLORS.find(c => c.key === chatPrefs.incomingBubbleColor) || BUBBLE_COLORS.find(c => c.key === 'gray');
            return {
                '--bubble-color': bub.color,
                '--bubble-bg': bub.bg,
                '--bubble-text': bub.text,
                '--incoming-bg': inc.bg,
                '--incoming-text': inc.text,
            };
        })()}>
            {/* Sidebar de conversaciones */}
            <motion.aside
                className={`chat-sidebar ${showMobileChat ? 'mobile-hidden' : ''}`}
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
            >
                {/* Header */}
                <div className="chat-sidebar-header">
                    <h2>
                        <MessageCircle size={24} />
                        Mensajes
                    </h2>
                    <div className="chat-header-actions">
                        <motion.button
                            className="create-group-btn"
                            onClick={() => setShowCreateGroup(true)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title="Crear grupo"
                            style={{ cursor: 'pointer' }}
                        >
                            <Users size={16} />
                            <span>Crear grupo</span>
                        </motion.button>
                        <motion.button
                            className="new-chat-btn"
                            onClick={() => setShowNewChat(true)}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Nueva conversación"
                            style={{ cursor: 'pointer' }}
                        >
                            <Plus size={20} />
                        </motion.button>
                    </div>
                </div>

                {/* Búsqueda */}
                <div className="chat-search">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Buscar conversación..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                {/* Estado de conexión y presencia */}
                <div className="connection-presence-row">
                    <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
                        <span className="status-dot" />
                        {connected ? 'Conectado' : 'Desconectado'}
                    </div>
                    <button className="presence-btn" onClick={() => setShowPresenceMenu(true)} title="Cambiar estado">
                        <Circle size={8} fill={
                            userPresence[user?.id] === 'reunion' ? '#f59e0b' :
                            userPresence[user?.id] === 'almorzando' ? '#f97316' :
                            userPresence[user?.id] === 'no_molestar' ? '#ef4444' : '#22c55e'
                        } color={
                            userPresence[user?.id] === 'reunion' ? '#f59e0b' :
                            userPresence[user?.id] === 'almorzando' ? '#f97316' :
                            userPresence[user?.id] === 'no_molestar' ? '#ef4444' : '#22c55e'
                        } />
                        <span>{
                            userPresence[user?.id] === 'reunion' ? 'En reunión' :
                            userPresence[user?.id] === 'almorzando' ? 'Almorzando' :
                            userPresence[user?.id] === 'no_molestar' ? 'No molestar' : 'Disponible'
                        }</span>
                    </button>
                </div>

                {/* Lista de conversaciones */}
                <div className="conversations-list">
                    {/* Guardados */}
                    <div
                        className={`conversation-item bookmarks-item ${showBookmarks ? 'active' : ''}`}
                        onClick={() => {
                            setShowBookmarks(true);
                            fetchBookmarks().then(bms => {
                                setBookmarks(bms);
                                setBookmarkedMessageIds(new Set(bms.map(b => b.id)));
                            });
                        }}
                    >
                        <div className="conversation-avatar bookmarks-avatar">
                            <Bookmark size={20} />
                        </div>
                        <div className="conversation-info">
                            <div className="conversation-name">Guardados</div>
                            <div className="conversation-preview">
                                {bookmarks.length > 0
                                    ? `${bookmarks.length} mensaje${bookmarks.length !== 1 ? 's' : ''}`
                                    : 'Guarda mensajes importantes'}
                            </div>
                        </div>
                    </div>
                    <div className="conversations-divider" />

                    {/* Grupos anclados arriba */}
                    {groupConversations.length > 0 && (
                        <>
                            <div className="conversations-section-label">
                                <Users size={14} />
                                Grupos
                            </div>
                            {groupConversations.map((conv) => (
                                    <div
                                        key={conv.id}
                                        className={`conversation-item ${activeConversation?.id === conv.id ? 'active' : ''}`}
                                        onClick={() => handleSelectConversation(conv)}
                                    >
                                        <div className="conversation-avatar">
                                            <Users size={20} />
                                        </div>
                                        <div className="conversation-info">
                                            <div className="conversation-name">{conv.name}</div>
                                            <div className="conversation-preview">
                                                {(() => {
                                                    const typing = Object.keys(typingByConv[conv.id] || {});
                                                    if (typing.length > 0) {
                                                        return <span className="conversation-typing">{typing[0]} escribiendo…</span>;
                                                    }
                                                    return (
                                                        <>
                                                            {conv.last_message?.sender_id === user?.id && (
                                                                <span className="conversation-read-status">
                                                                    {conv.last_message.is_read ? <CheckCheck size={13} /> : <Check size={13} />}
                                                                </span>
                                                            )}
                                                            {getMessagePreview(conv.last_message)}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <div className="conversation-meta">
                                            <span className="conversation-time">
                                                {conv.last_message ? formatSidebarTime(conv.last_message.created_at) : ''}
                                            </span>
                                            {conv.unread_count > 0 && (
                                                <motion.span
                                                    className="unread-badge"
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                >
                                                    {conv.unread_count}
                                                </motion.span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            {directConversations.length > 0 && (
                                <div className="conversations-divider" />
                            )}
                        </>
                    )}

                    {/* Chats individuales */}
                    {directConversations.length > 0 && (
                        <>
                            {groupConversations.length > 0 && (
                                <div className="conversations-section-label">
                                    <MessageCircle size={14} />
                                    Mensajes
                                </div>
                            )}
                            {directConversations.map((conv) => (
                                    <div
                                        key={conv.id}
                                        className={`conversation-item ${activeConversation?.id === conv.id ? 'active' : ''}`}
                                        onClick={() => handleSelectConversation(conv)}
                                    >
                                        <div className="conversation-avatar">
                                            {(() => {
                                                const otherParticipant = conv.participants?.find(p => p.id !== user?.id);
                                                const otherId = otherParticipant?.id;
                                                const isOnline = otherId && onlineUsers.has(otherId);
                                                const presence = otherId ? userPresence[otherId] : null;
                                                const presenceColor = presence === 'reunion' ? '#f59e0b' :
                                                    presence === 'almorzando' ? '#f97316' :
                                                    presence === 'no_molestar' ? '#ef4444' : '#22c55e';
                                                const presenceLabel = presence === 'reunion' ? 'En reunión' :
                                                    presence === 'almorzando' ? 'Almorzando' :
                                                    presence === 'no_molestar' ? 'No molestar' : 'Disponible';
                                                return (
                                                    <>
                                                        {otherParticipant?.foto ? (
                                                            <>
                                                                <img
                                                                    src={otherParticipant.foto}
                                                                    alt={conv.name}
                                                                    className="avatar-img-chat"
                                                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                                />
                                                                <span style={{ display: 'none' }}>{conv.name?.charAt(0).toUpperCase()}</span>
                                                            </>
                                                        ) : (
                                                            <span>{conv.name?.charAt(0).toUpperCase()}</span>
                                                        )}
                                                        {isOnline && (
                                                            <span
                                                                className="conversation-presence-dot"
                                                                style={{ background: presenceColor, boxShadow: `0 0 0 2px #fff` }}
                                                                title={presenceLabel}
                                                            />
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                        <div className="conversation-info">
                                            <div className="conversation-name">{conv.name}</div>
                                            <div className="conversation-preview">
                                                {(() => {
                                                    const typing = Object.keys(typingByConv[conv.id] || {});
                                                    if (typing.length > 0) {
                                                        return <span className="conversation-typing">escribiendo…</span>;
                                                    }
                                                    return (
                                                        <>
                                                            {conv.last_message?.sender_id === user?.id && (
                                                                <span className="conversation-read-status">
                                                                    {conv.last_message.is_read ? <CheckCheck size={13} /> : <Check size={13} />}
                                                                </span>
                                                            )}
                                                            {getMessagePreview(conv.last_message)}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <div className="conversation-meta">
                                            <span className="conversation-time">
                                                {conv.last_message ? formatSidebarTime(conv.last_message.created_at) : ''}
                                            </span>
                                            {conv.unread_count > 0 && (
                                                <motion.span
                                                    className="unread-badge"
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                >
                                                    {conv.unread_count}
                                                </motion.span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </>
                    )}

                    {filteredConversations.length === 0 && (
                        <div className="no-conversations">
                            <MessageCircle size={48} strokeWidth={1} />
                            <p>No hay conversaciones</p>
                            <button onClick={() => setShowNewChat(true)}>
                                Iniciar chat
                            </button>
                        </div>
                    )}
                </div>
            </motion.aside>

            {/* Área principal de chat */}
            <main
                className={`chat-main ${showMobileChat ? 'mobile-visible' : ''} ${buzzActive ? 'buzz-shake' : ''}`}
                onDragEnter={activeConversation ? handleDragEnter : undefined}
                onDragLeave={activeConversation ? handleDragLeave : undefined}
                onDragOver={activeConversation ? handleDragOver : undefined}
                onDrop={activeConversation ? handleDrop : undefined}
            >
                <AnimatePresence>
                    {buzzActive && (
                        <motion.div
                            className="buzz-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <motion.div
                                className="buzz-overlay-content"
                                initial={{ scale: 0.5, opacity: 0 }}
                                animate={{ scale: [0.5, 1.2, 1], opacity: 1 }}
                                exit={{ scale: 0.5, opacity: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <Zap size={32} />
                                <span>¡Zumbido de {buzzActive.sender_name}!</span>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
                {isDragging && (
                    <div className="drag-overlay">
                        <div className="drag-overlay-content">
                            <Paperclip size={48} strokeWidth={1.5} />
                            <p>Suelta los archivos aquí</p>
                        </div>
                    </div>
                )}
                {showBookmarks ? (
                    <>
                        {/* Header de Guardados */}
                        <div className="chat-header">
                            <button
                                className="back-btn mobile-only"
                                onClick={() => { setShowBookmarks(false); setShowMobileChatWithReason(false, 'bookmarks_back'); }}
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div className="conversation-avatar bookmarks-avatar" style={{ width: 36, height: 36 }}>
                                <Bookmark size={18} />
                            </div>
                            <div className="chat-header-info">
                                <h3>Guardados</h3>
                                <span className="chat-status">{bookmarks.length} mensaje{bookmarks.length !== 1 ? 's' : ''}</span>
                            </div>
                        </div>

                        {/* Lista de mensajes guardados */}
                        <div className="chat-messages bookmarks-messages">
                            {bookmarks.length === 0 ? (
                                <div className="no-messages">
                                    <Bookmark size={48} strokeWidth={1} />
                                    <p>No tienes mensajes guardados</p>
                                    <span style={{ fontSize: '0.8rem', color: '#94a3b8', maxWidth: 260, textAlign: 'center' }}>
                                        Guarda mensajes desde el menú de cualquier conversación, o escribe una nota tuya abajo.
                                    </span>
                                </div>
                            ) : (
                                bookmarks.map((msg) => msg.is_note ? (
                                    <div key={msg.id} className="bookmark-message-card bookmark-note">
                                        <div className="bookmark-origin">
                                            <Bookmark size={12} />
                                            <span>Nota personal</span>
                                            <span className="bookmark-date">{msg.bookmarked_at ? new Date(msg.bookmarked_at).toLocaleString('es-VE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                        </div>
                                        {msg.content && <div className="bookmark-content">{msg.content}</div>}
                                        {msg.file && (() => {
                                            const fileUrl = `${BASE_URL}${msg.file.download_url}?token=${authToken}`;
                                            return msg.file.mime_type?.startsWith('image/') ? (
                                                <img
                                                    src={fileUrl}
                                                    alt={msg.file.original_name}
                                                    className="bookmark-note-image"
                                                    onClick={() => window.open(fileUrl, '_blank')}
                                                />
                                            ) : (
                                                <a
                                                    href={fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="bookmark-file"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {getFileIcon(msg.file.mime_type, msg.file.original_name)}
                                                    <span>{msg.file.original_name}</span>
                                                </a>
                                            );
                                        })()}
                                        <button
                                            className="bookmark-remove-btn"
                                            onClick={() => handleRemoveNote(msg.bookmark_id)}
                                            title="Eliminar nota"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div key={msg.id} className="bookmark-message-card" onClick={() => {
                                        if (msg.origin_conversation) {
                                            setShowBookmarks(false);
                                            joinConversation(msg.origin_conversation.id);
                                        }
                                    }}>
                                        <div className="bookmark-origin">
                                            {msg.origin_conversation?.is_group ? <Users size={12} /> : <MessageCircle size={12} />}
                                            <span>{msg.origin_conversation?.name || 'Chat'}</span>
                                            <span className="bookmark-date">{msg.bookmarked_at ? new Date(msg.bookmarked_at).toLocaleString('es-VE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                        </div>
                                        <div className="bookmark-sender">{msg.sender_name}</div>
                                        {msg.content && (
                                            <div className="bookmark-content">{msg.content}</div>
                                        )}
                                        {msg.file && (
                                            <div className="bookmark-file">
                                                {getFileIcon(msg.file.mime_type, msg.file.original_name)}
                                                <span>{msg.file.original_name}</span>
                                            </div>
                                        )}
                                        <button
                                            className="bookmark-remove-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleBookmark(msg.id);
                                            }}
                                            title="Quitar de guardados"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Input para notas propias */}
                        <div className="bookmarks-note-input-wrapper">
                            {noteFile && (
                                <div className="bookmarks-note-attachment">
                                    {noteFile.type?.startsWith('image/') ? (
                                        <img src={URL.createObjectURL(noteFile)} alt="adjunto" />
                                    ) : (
                                        <div className="bookmarks-note-attachment-file">
                                            {getFileIcon(noteFile.type, noteFile.name)}
                                            <span>{noteFile.name}</span>
                                        </div>
                                    )}
                                    <button onClick={() => setNoteFile(null)} title="Quitar">
                                        <X size={14} />
                                    </button>
                                </div>
                            )}
                            <div className="bookmarks-note-input">
                                <button
                                    className="note-attach-btn"
                                    onClick={() => noteFileInputRef.current?.click()}
                                    title="Adjuntar archivo o imagen"
                                    disabled={noteUploading}
                                >
                                    <Paperclip size={18} />
                                </button>
                                <input
                                    ref={noteFileInputRef}
                                    type="file"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (f) setNoteFile(f);
                                        e.target.value = '';
                                    }}
                                />
                                <textarea
                                    ref={noteTextareaRef}
                                    value={noteInput}
                                    onChange={(e) => {
                                        setNoteInput(e.target.value);
                                        adjustNoteTextareaHeight();
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleAddNote();
                                        }
                                    }}
                                    onPaste={handleNotePaste}
                                    placeholder="Escribe una nota para ti…"
                                    rows={1}
                                />
                                <button
                                    onClick={handleAddNote}
                                    disabled={(!noteInput.trim() && !noteFile) || noteUploading}
                                    title="Guardar nota"
                                >
                                    {noteUploading ? <Loader2 size={18} className="tk-spin" /> : <Send size={18} />}
                                </button>
                            </div>
                        </div>
                    </>
                ) : activeConversation ? (
                    <>
                        {/* Header del chat */}
                        <div className="chat-header">
                            <button
                                className="back-btn mobile-only"
                                onClick={() => setShowMobileChatWithReason(false, 'mobile_back_button')}
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div className="chat-header-avatar">
                                {activeConversation.is_group ? (
                                    <Users size={20} />
                                ) : (
                                    (() => {
                                        const otherParticipant = activeConversation.participants?.find(p => p.id !== user?.id);
                                        if (otherParticipant?.foto) {
                                            return (
                                                <>
                                                    <img 
                                                        src={otherParticipant.foto} 
                                                        alt={activeConversation.name} 
                                                        className="avatar-img-chat" 
                                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} 
                                                    />
                                                    <span style={{ display: 'none' }}>{activeConversation.name?.charAt(0).toUpperCase()}</span>
                                                </>
                                            );
                                        }
                                        return <span>{activeConversation.name?.charAt(0).toUpperCase()}</span>;
                                    })()
                                )}
                            </div>
                            <div className="chat-header-info">
                                <h3>{activeConversation.name}</h3>
                                {(!activeConversation.is_group && activeConversation.participants) && (() => {
                                    const otherParticipant = activeConversation.participants.find(p => p.id !== user?.id);
                                    if (otherParticipant && (otherParticipant.cargo || otherParticipant.area)) {
                                        return (
                                            <div className="chat-user-details" style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '-2px', marginBottom: '2px' }}>
                                                {otherParticipant.cargo} {otherParticipant.area ? `• ${otherParticipant.area}` : ''}
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                                <span className={`chat-status ${
                                    (() => {
                                        const typingHere = typingByConv[activeConversation.id] || {};
                                        if (Object.keys(typingHere).length > 0) return '';
                                        if (activeConversation.is_group) return '';
                                        const otherParticipant = activeConversation.participants?.find(p => p.id !== user?.id);
                                        return otherParticipant && onlineUsers.has(otherParticipant.id) ? '' : 'offline';
                                    })()
                                }`}>
                                    {Object.keys(typingByConv[activeConversation.id] || {}).length > 0
                                        ? `${Object.keys(typingByConv[activeConversation.id]).join(', ')} escribiendo...`
                                        : activeConversation.is_group
                                            ? `${activeConversation.participants?.length || 0} miembros, ${activeConversation.participants?.filter(p => onlineUsers.has(p.id)).length || 0} en línea`
                                            : (() => {
                                                const otherParticipant = activeConversation.participants?.find(p => p.id !== user?.id);
                                                if (!otherParticipant || !onlineUsers.has(otherParticipant.id)) return 'Desconectado';
                                                const presence = userPresence[otherParticipant.id];
                                                if (presence === 'reunion') return 'En reunión';
                                                if (presence === 'almorzando') return 'Almorzando';
                                                if (presence === 'no_molestar') return 'No molestar';
                                                return 'En línea';
                                            })()
                                    }
                                </span>
                            </div>
                            {!activeConversation.is_group && (
                                <>
                                    <button
                                        className="chat-call-btn"
                                        onClick={() => startCall(activeConversation.id, 'audio')}
                                        disabled={callStatus !== 'idle'}
                                        title="Llamada de audio"
                                    >
                                        <Phone size={20} />
                                    </button>
                                    <button
                                        className="chat-call-btn"
                                        onClick={() => startCall(activeConversation.id, 'video')}
                                        disabled={callStatus !== 'idle'}
                                        title="Videollamada"
                                    >
                                        <VideoIcon size={20} />
                                    </button>
                                </>
                            )}
                            {activeConversation.is_group && pinnedMessages.length > 0 && (
                                <button
                                    className={`chat-options ${showPinnedPanel ? 'active' : ''}`}
                                    onClick={() => setShowPinnedPanel(prev => !prev)}
                                    title={`${pinnedMessages.length} mensaje(s) fijado(s)`}
                                >
                                    <Pin size={20} />
                                    <span className="pin-count-badge">{pinnedMessages.length}</span>
                                </button>
                            )}
                            <button
                                className="chat-options"
                                onClick={() => {
                                    setShowMessageSearch(prev => !prev);
                                    if (showMessageSearch) {
                                        closeMessageSearch();
                                    } else {
                                        setTimeout(() => messageSearchInputRef.current?.focus(), 100);
                                    }
                                }}
                                title="Buscar mensajes"
                            >
                                <Search size={20} />
                            </button>
                            {activeConversation.is_group && (
                                <button
                                    className="chat-options catchup-btn"
                                    onClick={() => {
                                        setShowSummary('');
                                        setSummaryMeta(null);
                                    }}
                                    title="Ponme al día"
                                >
                                    <Sparkles size={18} />
                                </button>
                            )}
                            <div className="chat-options-wrapper">
                                <button
                                    className="chat-options"
                                    onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                                >
                                    <MoreVertical size={20} />
                                </button>
                                <AnimatePresence>
                                    {showOptionsMenu && (
                                        <motion.div
                                            className="options-dropdown"
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                        >
                                            {/* Group-specific options */}
                                            {activeConversation.is_group && (
                                                <>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => {
                                                            setShowGroupSettings(true);
                                                            setShowOptionsMenu(false);
                                                        }}
                                                    >
                                                        <Settings size={16} />
                                                        Ver grupo
                                                    </button>

                                                    {/* Owner-only options */}
                                                    {activeConversation.is_owner && (
                                                        <button
                                                            className="dropdown-item"
                                                            onClick={() => {
                                                                setShowInviteModal(true);
                                                                setShowOptionsMenu(false);
                                                            }}
                                                        >
                                                            <UserPlus size={16} />
                                                            Invitar usuarios
                                                        </button>
                                                    )}

                                                </>
                                            )}

                                            {/* Personalizar chat */}
                                            <button
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setShowCustomize(true);
                                                    setShowOptionsMenu(false);
                                                }}
                                            >
                                                <Palette size={16} />
                                                Personalizar
                                            </button>

                                            {/* Delete option - owner for groups, anyone for 1:1 */}
                                            {(!activeConversation.is_group || activeConversation.is_owner) && (
                                                <button
                                                    className="dropdown-item delete"
                                                    onClick={handleDeleteConversation}
                                                >
                                                    <Trash2 size={16} />
                                                    {activeConversation.is_group ? 'Eliminar grupo' : 'Eliminar conversación'}
                                                </button>
                                            )}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Barra de búsqueda de mensajes */}
                        <AnimatePresence>
                            {showMessageSearch && (
                                <motion.div
                                    className="message-search-bar"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <Search size={18} />
                                    <input
                                        ref={messageSearchInputRef}
                                        type="text"
                                        placeholder="Buscar en esta conversación..."
                                        value={messageSearchQuery}
                                        onChange={(e) => handleMessageSearch(e.target.value)}
                                    />
                                    {messageSearchQuery && (
                                        <span className="search-result-count">
                                            {messages.length} resultado{messages.length !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                    <button className="close-search-btn" onClick={closeMessageSearch}>
                                        <X size={16} />
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Panel de mensajes fijados */}
                        <AnimatePresence>
                            {showPinnedPanel && pinnedMessages.length > 0 && (
                                <motion.div
                                    className="pinned-panel"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <div className="pinned-panel-header">
                                        <Pin size={16} />
                                        <span>Mensajes fijados ({pinnedMessages.length})</span>
                                        <button onClick={() => setShowPinnedPanel(false)}><X size={16} /></button>
                                    </div>
                                    <div className="pinned-panel-list">
                                        {pinnedMessages.map(pm => (
                                            <div
                                                key={pm.id}
                                                className="pinned-message-item"
                                                onClick={() => {
                                                    setShowPinnedPanel(false);
                                                    const el = document.getElementById(`msg-${pm.id}`);
                                                    if (el) {
                                                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                        el.classList.add('highlight-flash');
                                                        setTimeout(() => el.classList.remove('highlight-flash'), 1500);
                                                    }
                                                }}
                                            >
                                                <span className="pinned-msg-sender">{pm.sender_name}</span>
                                                <span className="pinned-msg-text">
                                                    {pm.content
                                                        ? (isBuzzMessage(pm.content) ? '⚡ Zumbido' : isGifMessage(pm.content) ? 'GIF' : isStickerMessage(pm.content) ? 'Sticker' : isTicketSuggestMessage(pm.content) ? '🎫 Sugerencia de ticket' : isTicketCreatedMessage(pm.content) ? '🎫 Ticket creado' : pm.content.substring(0, 80))
                                                        : pm.file ? pm.file.original_name : ''}
                                                </span>
                                                <span className="pinned-msg-time">{formatTime(pm.created_at)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Mensajes */}
                        <div
                            className="chat-messages"
                            ref={messagesContainerRef}
                            onClick={(e) => { if (e.target === e.currentTarget) setSelectedMessageId(null); }}
                            style={{
                                ...(CHAT_BACKGROUNDS.find(b => b.key === chatPrefs.background)?.style || {}),
                                '--chat-font-size': FONT_SIZES.find(f => f.key === chatPrefs.fontSize)?.size || '0.875rem',
                            }}
                        >
                            {loading ? (
                                <div className="loading-messages">
                                    <div className="spinner" />
                                    Cargando mensajes...
                                </div>
                            ) : (
                                <>
                                    {hasMore && (
                                        <button
                                            className="load-more-btn"
                                            onClick={handleLoadMore}
                                            disabled={loadingMore}
                                        >
                                            {loadingMore ? (
                                                <><div className="spinner small" /> Cargando...</>
                                            ) : (
                                                <><ChevronUp size={16} /> Cargar mensajes anteriores</>
                                            )}
                                        </button>
                                    )}
                                    {(() => {
                                        let globalIndex = 0;
                                        return Object.entries(messageGroups).map(([date, msgs]) => (
                                            <div key={date} className="message-group">
                                                <div className="date-divider">
                                                    <span>{formatDate(msgs[0].created_at)}</span>
                                                </div>
                                                {msgs.map((msg) => {
                                                    const isRecent = globalIndex >= totalMessages - 5;
                                                    globalIndex++;
                                                    const MessageWrapper = isRecent ? motion.div : 'div';
                                                    const animProps = isRecent ? {
                                                        initial: { opacity: 0, y: 20, scale: 0.9 },
                                                        animate: { opacity: 1, y: 0, scale: 1 },
                                                        transition: { duration: 0.2 }
                                                    } : {};

                                                    // Mensaje de zumbido — renderizar como evento de sistema
                                                    if (isBuzzMessage(msg.content)) {
                                                        return (
                                                            <div key={msg.id} className="buzz-system-message">
                                                                <Zap size={14} />
                                                                <span>
                                                                    {msg.sender_id === user?.id
                                                                        ? 'Enviaste un zumbido'
                                                                        : `${msg.sender_name} envió un zumbido`}
                                                                </span>
                                                            </div>
                                                        );
                                                    }

                                                    // Mensaje de videollamada/llamada — pill especial
                                                    if (isCallMessage(msg.content)) {
                                                        return (
                                                            <div key={msg.id} className="buzz-system-message">
                                                                <CallMessageBubble message={msg} conversation={activeConversation} />
                                                            </div>
                                                        );
                                                    }

                                                    // Ticket creado desde chat — evento de sistema
                                                    if (isTicketCreatedMessage(msg.content)) {
                                                        const td = parseTicketData(msg.content);
                                                        return (
                                                            <div key={msg.id} className="buzz-system-message ticket-created-msg">
                                                                <Ticket size={14} />
                                                                <span>
                                                                    {msg.sender_id === user?.id ? 'Creaste' : `${msg.sender_name} creó`} el ticket <strong>#{td?.id}</strong> — {td?.titulo}
                                                                </span>
                                                            </div>
                                                        );
                                                    }

                                                    // Sugerencia de ticket desde chat — tarjeta interactiva
                                                    if (isTicketSuggestMessage(msg.content)) {
                                                        const td = parseTicketData(msg.content);
                                                        if (!td) return null;
                                                        const alreadyCreated = createdTicketIds.has(msg.id);
                                                        const catLabels = { hardware: 'Hardware', software: 'Software', red: 'Red', accesos: 'Accesos', otro: 'Otro' };
                                                        const prioLabels = { baja: 'Baja', media: 'Media', alta: 'Alta', critica: 'Critica' };
                                                        const prioColors = { baja: '#64748b', media: '#3b82f6', alta: '#f59e0b', critica: '#ef4444' };
                                                        return (
                                                            <div key={msg.id} className="ticket-suggest-card-wrapper">
                                                                <div className="ticket-suggest-card">
                                                                    <div className="tsc-header">
                                                                        <Ticket size={16} />
                                                                        <span>Soporte TI sugiere crear un ticket</span>
                                                                    </div>
                                                                    <h4 className="tsc-title">{td.titulo}</h4>
                                                                    <p className="tsc-desc">{td.descripcion}</p>
                                                                    <div className="tsc-badges">
                                                                        <span className="tsc-badge" style={{ color: '#0891b2', background: '#ecfeff' }}>
                                                                            <Monitor size={12} /> {catLabels[td.categoria] || td.categoria}
                                                                        </span>
                                                                        <span className="tsc-badge" style={{ color: prioColors[td.prioridad] || '#64748b', background: `${prioColors[td.prioridad] || '#64748b'}15` }}>
                                                                            {prioLabels[td.prioridad] || td.prioridad}
                                                                        </span>
                                                                    </div>
                                                                    {msg.sender_id !== user?.id && !alreadyCreated && (
                                                                        <button className="tsc-create-btn" onClick={() => handleCreateTicketFromChat(td, msg.id)}>
                                                                            <Ticket size={15} /> Crear Ticket
                                                                        </button>
                                                                    )}
                                                                    {alreadyCreated && (
                                                                        <div className="tsc-created">
                                                                            <Check size={14} /> Ticket creado
                                                                        </div>
                                                                    )}
                                                                    {msg.sender_id === user?.id && (
                                                                        <div className="tsc-sent-hint">Enviado — esperando que el usuario cree el ticket</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <MessageWrapper
                                                            key={msg.id}
                                                            id={`msg-${msg.id}`}
                                                            className={`message ${msg.sender_id === user?.id ? 'outgoing' : 'incoming'}${msg.is_deleted ? ' deleted' : ''}${selectedMessageId === msg.id ? ' selected' : ''}${msg.is_pinned ? ' pinned' : ''}`}
                                                            {...animProps}
                                                            onClick={(e) => {
                                                                if (!msg.is_deleted) {
                                                                    const isOpening = selectedMessageId !== msg.id;
                                                                    setSelectedMessageId(prev => prev === msg.id ? null : msg.id);
                                                                    setShowReactionPicker(null);
                                                                    if (isOpening) {
                                                                        const msgEl = e.currentTarget;
                                                                        const container = messagesContainerRef.current;
                                                                        if (msgEl && container) {
                                                                            const msgRect = msgEl.getBoundingClientRect();
                                                                            const containerRect = container.getBoundingClientRect();
                                                                            setMenuFlip(msgRect.top - containerRect.top < 200);
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            {msg.is_pinned && (
                                                                <div className="pinned-indicator">
                                                                    <Pin size={12} /> Mensaje fijado
                                                                </div>
                                                            )}

                                                            {msg.sender_id !== user?.id && (
                                                                <span className="message-sender">{msg.sender_name}</span>
                                                            )}

                                                            {/* Mensaje citado (reply) */}
                                                            {msg.reply_to && (
                                                                <div className="reply-preview" onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const el = document.getElementById(`msg-${msg.reply_to.id}`);
                                                                    if (el) {
                                                                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                                        el.classList.add('highlight-flash');
                                                                        setTimeout(() => el.classList.remove('highlight-flash'), 1500);
                                                                    }
                                                                }}>
                                                                    <div className="reply-bar" />
                                                                    <div className="reply-content">
                                                                        <span className="reply-sender">{msg.reply_to.sender_name}</span>
                                                                        <span className="reply-text">
                                                                            {msg.reply_to.is_deleted
                                                                                ? 'Mensaje eliminado'
                                                                                : msg.reply_to.content
                                                                                    ? (isBuzzMessage(msg.reply_to.content) ? '⚡ Zumbido' : isGifMessage(msg.reply_to.content) ? 'GIF' : isStickerMessage(msg.reply_to.content) ? 'Sticker' : isTicketSuggestMessage(msg.reply_to.content) ? '🎫 Sugerencia de ticket' : isTicketCreatedMessage(msg.reply_to.content) ? '🎫 Ticket creado' : msg.reply_to.content.substring(0, 100))
                                                                                    : msg.reply_to.file ? msg.reply_to.file.original_name : ''}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {msg.is_deleted ? (
                                                                <p className="message-content deleted-text">
                                                                    <Ban size={14} />
                                                                    {msg.sender_id === user?.id
                                                                        ? 'Eliminaste este mensaje'
                                                                        : 'Este mensaje fue eliminado'}
                                                                </p>
                                                            ) : (
                                                                <>
                                                                    {msg.file && (
                                                                        <div className="message-file" onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const idx = conversationFiles.findIndex(f => f.file.uuid === msg.file.uuid);
                                                                            setViewerFile({
                                                                                file: msg.file,
                                                                                senderName: msg.sender_name,
                                                                                timestamp: formatTime(msg.created_at),
                                                                                index: idx >= 0 ? idx : 0
                                                                            });
                                                                        }}>
                                                                            {msg.file.mime_type?.startsWith('image/') ? (
                                                                                <img
                                                                                    src={`${BASE_URL}${msg.file.download_url}?token=${authToken}`}
                                                                                    alt={msg.file.original_name}
                                                                                    className="message-image"
                                                                                />
                                                                            ) : isPdfFile(msg.file.mime_type, msg.file.original_name) ? (
                                                                                <div className="pdf-attachment">
                                                                                    <PdfThumbnail
                                                                                        fileUuid={msg.file.uuid}
                                                                                        fileUrl={`${BASE_URL}${msg.file.download_url}`}
                                                                                        token={authToken}
                                                                                        fileName={msg.file.original_name}
                                                                                        width={220}
                                                                                        className="pdf-thumb pdf-thumb-message"
                                                                                        imageClassName="pdf-thumb-image pdf-thumb-image-message"
                                                                                        badgeClassName="pdf-thumb-badge"
                                                                                        placeholderClassName="pdf-thumb-placeholder pdf-thumb-placeholder-message"
                                                                                    />
                                                                                    <div className="pdf-attachment-meta">
                                                                                        <span className="pdf-attachment-name">{msg.file.original_name}</span>
                                                                                    </div>
                                                                                </div>
                                                                            ) : isDocPreviewable(msg.file.mime_type, msg.file.original_name) ? (
                                                                                <div className="doc-attachment">
                                                                                    <DocPreview
                                                                                        fileUuid={msg.file.uuid}
                                                                                        fileUrl={`${BASE_URL}${msg.file.download_url}`}
                                                                                        token={authToken}
                                                                                        fileName={msg.file.original_name}
                                                                                        mimeType={msg.file.mime_type}
                                                                                        width={220}
                                                                                        className="doc-thumb doc-thumb-message"
                                                                                        imageClassName="doc-thumb-image"
                                                                                        badgeClassName={`doc-thumb-badge ${isExcelFile(msg.file.mime_type, msg.file.original_name) ? 'doc-thumb-badge-excel' : 'doc-thumb-badge-word'}`}
                                                                                        placeholderClassName="doc-thumb-placeholder doc-thumb-placeholder-message"
                                                                                    />
                                                                                    <div className="doc-attachment-meta">
                                                                                        <span className="doc-attachment-name">{msg.file.original_name}</span>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="file-attachment">
                                                                                    {getFileIcon(msg.file.mime_type, msg.file.original_name)}
                                                                                    <span>{msg.file.original_name}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {msg.content && (
                                                                        isGifMessage(msg.content) ? (
                                                                            <img
                                                                                src={getGifUrl(msg.content)}
                                                                                alt="GIF"
                                                                                className="message-gif"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                loading="lazy"
                                                                            />
                                                                        ) : isStickerMessage(msg.content) ? (
                                                                            <img
                                                                                src={getStickerUrl(msg.content)}
                                                                                alt="Sticker"
                                                                                className="message-sticker"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                loading="lazy"
                                                                            />
                                                                        ) : (
                                                                            <div className="message-content">
                                                                                {messageSearchQuery
                                                                                    ? highlightText(msg.content, messageSearchQuery)
                                                                                    : renderMessageContent(msg.content)}
                                                                            </div>
                                                                        )
                                                                    )}

                                                                    {/* Link preview */}
                                                                    {msg.link_preview && (
                                                                        <a
                                                                            href={msg.link_preview.url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="link-preview-card"
                                                                            onClick={e => e.stopPropagation()}
                                                                        >
                                                                            {msg.link_preview.image_url && (
                                                                                <div className="link-preview-image">
                                                                                    <img
                                                                                        src={msg.link_preview.image_url}
                                                                                        alt=""
                                                                                        onError={e => { e.target.parentElement.style.display = 'none'; }}
                                                                                        loading="lazy"
                                                                                    />
                                                                                </div>
                                                                            )}
                                                                            <div className="link-preview-text">
                                                                                <span className="link-preview-domain">{msg.link_preview.domain}</span>
                                                                                {msg.link_preview.title && <span className="link-preview-title">{msg.link_preview.title}</span>}
                                                                                {msg.link_preview.description && <span className="link-preview-description">{msg.link_preview.description}</span>}
                                                                            </div>
                                                                        </a>
                                                                    )}

                                                                    {/* Encuesta inline */}
                                                                    {msg.poll && (
                                                                        <div className="chat-poll" onClick={(e) => e.stopPropagation()}>
                                                                            <div className="poll-question">
                                                                                <BarChart3 size={14} />
                                                                                <span>{msg.poll.question}</span>
                                                                            </div>
                                                                            <div className="poll-options">
                                                                                {msg.poll.options.map(opt => {
                                                                                    const pct = msg.poll.total_votes > 0
                                                                                        ? Math.round((opt.votes / msg.poll.total_votes) * 100)
                                                                                        : 0;
                                                                                    const hasVoted = opt.voters?.some(v => v.user_id === user?.id);
                                                                                    return (
                                                                                        <button
                                                                                            key={opt.id}
                                                                                            className={`poll-option ${hasVoted ? 'voted' : ''}`}
                                                                                            onClick={() => votePoll(msg.poll.id, opt.id)}
                                                                                            disabled={msg.poll.is_closed}
                                                                                        >
                                                                                            <div className="poll-option-bar" style={{ width: `${pct}%` }} />
                                                                                            <span className="poll-option-text">{opt.text}</span>
                                                                                            <span className="poll-option-pct">{pct}%</span>
                                                                                        </button>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                            <div className="poll-footer">
                                                                                {msg.poll.total_votes} voto{msg.poll.total_votes !== 1 ? 's' : ''}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Reacciones */}
                                                                    {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                                                                        <div className="message-reactions">
                                                                            {Object.entries(msg.reactions).map(([emoji, users]) => (
                                                                                <button
                                                                                    key={emoji}
                                                                                    className={`reaction-badge ${users.some(u => u.user_id === user?.id) ? 'own' : ''}`}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        toggleReaction(msg.id, emoji);
                                                                                    }}
                                                                                    title={users.map(u => u.user_name).join(', ')}
                                                                                >
                                                                                    <span>{emoji}</span>
                                                                                    <span className="reaction-count">{users.length}</span>
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}

                                                            <div className="message-meta">
                                                                {msg.edited_at && <span className="message-edited">editado</span>}
                                                                <span className="message-time">{formatTime(msg.created_at)}</span>
                                                                {msg.sender_id === user?.id && !msg.is_deleted && (
                                                                    <span className="message-status">
                                                                        {msg.is_read ? <CheckCheck size={14} /> : <Check size={14} />}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {/* Menú de acciones del mensaje */}
                                                            <AnimatePresence>
                                                                {selectedMessageId === msg.id && !msg.is_deleted && (
                                                                    <motion.div
                                                                        className={`message-actions ${menuFlip ? 'flip' : ''}`}
                                                                        initial={{ opacity: 0, scale: 0.8 }}
                                                                        animate={{ opacity: 1, scale: 1 }}
                                                                        exit={{ opacity: 0, scale: 0.8 }}
                                                                        transition={{ duration: 0.15 }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {/* Quick reactions */}
                                                                        <div className="quick-reactions">
                                                                            {QUICK_REACTIONS.map(emoji => (
                                                                                <button
                                                                                    key={emoji}
                                                                                    className="quick-reaction-btn"
                                                                                    onClick={() => {
                                                                                        toggleReaction(msg.id, emoji);
                                                                                        setSelectedMessageId(null);
                                                                                    }}
                                                                                >
                                                                                    {emoji}
                                                                                </button>
                                                                            ))}
                                                                            <button
                                                                                className="quick-reaction-btn more"
                                                                                onClick={() => setShowReactionPicker(showReactionPicker === msg.id ? null : msg.id)}
                                                                            >
                                                                                <SmilePlus size={18} />
                                                                            </button>
                                                                        </div>
                                                                        <div className="action-buttons">
                                                                            <button
                                                                                className="action-btn"
                                                                                onClick={() => {
                                                                                    setReplyingTo(msg);
                                                                                    setSelectedMessageId(null);
                                                                                    textareaRef.current?.focus();
                                                                                }}
                                                                            >
                                                                                <Reply size={16} />
                                                                                <span>Responder</span>
                                                                            </button>
                                                                            <button
                                                                                className="action-btn"
                                                                                onClick={() => {
                                                                                    setForwardingMessage(msg);
                                                                                    setSelectedMessageId(null);
                                                                                    setForwardSearch('');
                                                                                }}
                                                                            >
                                                                                <Forward size={16} />
                                                                                <span>Reenviar</span>
                                                                            </button>
                                                                            <button
                                                                                className={`action-btn ${bookmarkedMessageIds.has(msg.id) ? 'active' : ''}`}
                                                                                onClick={() => {
                                                                                    handleToggleBookmark(msg.id);
                                                                                    setSelectedMessageId(null);
                                                                                }}
                                                                            >
                                                                                {bookmarkedMessageIds.has(msg.id) ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                                                                                <span>{bookmarkedMessageIds.has(msg.id) ? 'Guardado' : 'Guardar'}</span>
                                                                            </button>
                                                                            <div className="reminder-btn-wrapper">
                                                                                <button
                                                                                    className="action-btn"
                                                                                    onClick={() => setReminderMenuMsgId(reminderMenuMsgId === msg.id ? null : msg.id)}
                                                                                >
                                                                                    <Clock3 size={16} />
                                                                                    <span>Recordar</span>
                                                                                </button>
                                                                                {reminderMenuMsgId === msg.id && (
                                                                                    <div className="reminder-submenu">
                                                                                        <button onClick={() => handleSetReminder(msg.id, '30min')}>En 30 min</button>
                                                                                        <button onClick={() => handleSetReminder(msg.id, '1h')}>En 1 hora</button>
                                                                                        <button onClick={() => handleSetReminder(msg.id, '3h')}>En 3 horas</button>
                                                                                        <button onClick={() => handleSetReminder(msg.id, 'tomorrow')}>Mañana 9:00 AM</button>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            {msg.sender_id === user?.id && msg.content && !isGifMessage(msg.content) && !isStickerMessage(msg.content) && (
                                                                                <button
                                                                                    className="action-btn"
                                                                                    onClick={() => {
                                                                                        setEditingMessage(msg);
                                                                                        setMessageInput(msg.content);
                                                                                        setSelectedMessageId(null);
                                                                                        textareaRef.current?.focus();
                                                                                    }}
                                                                                >
                                                                                    <Pencil size={16} />
                                                                                    <span>Editar</span>
                                                                                </button>
                                                                            )}
                                                                            {activeConversation?.is_group && activeConversation?.is_owner && (
                                                                                <button
                                                                                    className={`action-btn ${msg.is_pinned ? 'active' : ''}`}
                                                                                    onClick={() => {
                                                                                        togglePin(msg.id);
                                                                                        setSelectedMessageId(null);
                                                                                    }}
                                                                                >
                                                                                    <Pin size={16} />
                                                                                    <span>{msg.is_pinned ? 'Desfijar' : 'Fijar'}</span>
                                                                                </button>
                                                                            )}
                                                                            {msg.sender_id === user?.id && (
                                                                                <button
                                                                                    className="action-btn delete"
                                                                                    onClick={() => {
                                                                                        deleteMessage(msg.id);
                                                                                        setSelectedMessageId(null);
                                                                                    }}
                                                                                >
                                                                                    <Trash2 size={16} />
                                                                                    <span>Eliminar</span>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </motion.div>
                                                                )}
                                                            </AnimatePresence>

                                                            {/* Full emoji picker for reactions */}
                                                            <AnimatePresence>
                                                                {showReactionPicker === msg.id && (
                                                                    <motion.div
                                                                        className="reaction-emoji-picker"
                                                                        ref={reactionPickerRef}
                                                                        initial={{ opacity: 0, scale: 0.9 }}
                                                                        animate={{ opacity: 1, scale: 1 }}
                                                                        exit={{ opacity: 0, scale: 0.9 }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <EmojiPicker
                                                                            onEmojiClick={(emojiData) => {
                                                                                toggleReaction(msg.id, emojiData.emoji);
                                                                                setShowReactionPicker(null);
                                                                                setSelectedMessageId(null);
                                                                            }}
                                                                            width={300}
                                                                            height={350}
                                                                            searchPlaceholder="Buscar emoji..."
                                                                            previewConfig={{ showPreview: false }}
                                                                        />
                                                                    </motion.div>
                                                                )}
                                                            </AnimatePresence>
                                                        </MessageWrapper>
                                                    );
                                                })}
                                            </div>
                                        ));
                                    })()}
                                </>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input de mensaje */}
                        <div className="chat-input-container">
                            {/* Reply bar */}
                            <AnimatePresence>
                                {replyingTo && (
                                    <motion.div
                                        className="reply-bar-input"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                    >
                                        <Reply size={16} />
                                        <div className="reply-bar-content">
                                            <span className="reply-bar-sender">{replyingTo.sender_name}</span>
                                            <span className="reply-bar-text">
                                                {replyingTo.content
                                                    ? (isBuzzMessage(replyingTo.content) ? '⚡ Zumbido' : isGifMessage(replyingTo.content) ? 'GIF' : isStickerMessage(replyingTo.content) ? 'Sticker' : isTicketSuggestMessage(replyingTo.content) ? '🎫 Sugerencia de ticket' : isTicketCreatedMessage(replyingTo.content) ? '🎫 Ticket creado' : replyingTo.content.substring(0, 80))
                                                    : replyingTo.file ? replyingTo.file.original_name : ''}
                                            </span>
                                        </div>
                                        <button onClick={() => setReplyingTo(null)}>
                                            <X size={16} />
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Edit bar */}
                            <AnimatePresence>
                                {editingMessage && (
                                    <motion.div
                                        className="reply-bar-input editing-bar"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                    >
                                        <Pencil size={16} />
                                        <div className="reply-bar-content">
                                            <span className="reply-bar-sender">Editando mensaje</span>
                                            <span className="reply-bar-text">
                                                {editingMessage.content?.substring(0, 80)}
                                            </span>
                                        </div>
                                        <button onClick={() => { setEditingMessage(null); setMessageInput(''); }}>
                                            <X size={16} />
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Preview de archivos */}
                            {selectedFiles.length > 0 && (
                                <motion.div
                                    className="files-preview"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                >
                                    <div className="files-preview-list">
                                        {selectedFiles.map((file, idx) => (
                                            <div key={idx} className="file-preview-item">
                                                {filePreviews[idx] ? (
                                                    <img src={filePreviews[idx]} alt="Preview" />
                                                ) : isPdfFile(file.type, file.name) ? (
                                                    <PdfThumbnail
                                                        file={file}
                                                        fileName={file.name}
                                                        width={90}
                                                        className="pdf-thumb pdf-thumb-composer"
                                                        imageClassName="pdf-thumb-image pdf-thumb-image-composer"
                                                        badgeClassName="pdf-thumb-badge"
                                                        placeholderClassName="pdf-thumb-placeholder pdf-thumb-placeholder-composer"
                                                    />
                                                ) : isDocPreviewable(file.type, file.name) ? (
                                                    <DocPreview
                                                        file={file}
                                                        fileName={file.name}
                                                        mimeType={file.type}
                                                        width={90}
                                                        maxRows={4}
                                                        className="doc-thumb doc-thumb-composer"
                                                        imageClassName="doc-thumb-image"
                                                        badgeClassName={`doc-thumb-badge ${isExcelFile(file.type, file.name) ? 'doc-thumb-badge-excel' : 'doc-thumb-badge-word'}`}
                                                        placeholderClassName="doc-thumb-placeholder doc-thumb-placeholder-composer"
                                                    />
                                                ) : (
                                                    <div className="file-info">
                                                        <File size={20} />
                                                        <span>{file.name}</span>
                                                    </div>
                                                )}
                                                <button
                                                    className="file-remove-btn"
                                                    onClick={() => {
                                                        setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
                                                        setFilePreviews(prev => prev.filter((_, i) => i !== idx));
                                                    }}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        className="files-clear-all"
                                        onClick={() => { setSelectedFiles([]); setFilePreviews([]); }}
                                    >
                                        Quitar todos
                                    </button>
                                </motion.div>
                            )}

                            <div className="chat-input">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    multiple
                                    hidden
                                />
                                <motion.button
                                    type="button"
                                    className="attach-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                >
                                    <Paperclip size={20} />
                                </motion.button>
                                <div className="textarea-mention-wrapper">
                                    {/* Mention autocomplete dropdown */}
                                    <AnimatePresence>
                                        {mentionQuery !== null && activeConversation && (
                                            <motion.div
                                                className="mention-dropdown"
                                                initial={{ opacity: 0, y: 5 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 5 }}
                                            >
                                                {(activeConversation.participants || [])
                                                    .filter(p => p.id !== user?.id && incluyeTexto(p.nombre, mentionQuery))
                                                    .slice(0, 6)
                                                    .map((p, idx) => (
                                                        <div
                                                            key={p.id}
                                                            className={`mention-item ${idx === mentionIndex ? 'active' : ''}`}
                                                            onClick={() => insertMention(p)}
                                                        >
                                                            <span className="mention-name">{p.nombre}</span>
                                                            {p.cargo && <span className="mention-cargo">{p.cargo}</span>}
                                                        </div>
                                                    ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                    <textarea
                                        ref={textareaRef}
                                        placeholder="Escribe un mensaje..."
                                        value={messageInput}
                                        rows={1}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setMessageInput(val);
                                            handleTyping();
                                            adjustTextareaHeight();

                                            // Detectar menciones @
                                            const cursorPos = e.target.selectionStart;
                                            const textBeforeCursor = val.substring(0, cursorPos);
                                            const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
                                            if (mentionMatch) {
                                                setMentionQuery(mentionMatch[1]);
                                                setMentionIndex(0);
                                            } else {
                                                setMentionQuery(null);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            // Manejar navegación de menciones
                                            if (mentionQuery !== null) {
                                                const filtered = (activeConversation?.participants || [])
                                                    .filter(p => p.id !== user?.id && incluyeTexto(p.nombre, mentionQuery))
                                                    .slice(0, 6);
                                                if (e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                    setMentionIndex(prev => Math.min(prev + 1, filtered.length - 1));
                                                    return;
                                                }
                                                if (e.key === 'ArrowUp') {
                                                    e.preventDefault();
                                                    setMentionIndex(prev => Math.max(prev - 1, 0));
                                                    return;
                                                }
                                                if ((e.key === 'Enter' || e.key === 'Tab') && filtered[mentionIndex]) {
                                                    e.preventDefault();
                                                    insertMention(filtered[mentionIndex]);
                                                    return;
                                                }
                                                if (e.key === 'Escape') {
                                                    setMentionQuery(null);
                                                    return;
                                                }
                                            }
                                            handleKeyDown(e);
                                        }}
                                        onPaste={handlePaste}
                                    />
                                </div>

                                <div className="emoji-picker-wrapper">
                                    <AnimatePresence>
                                        {showEmojiPicker && (
                                            <motion.div
                                                className="emoji-picker-popover"
                                                ref={emojiPickerRef}
                                                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                                exit={{ opacity: 0, scale: 0.9, y: 10 }}
                                                transition={{ duration: 0.15 }}
                                            >
                                                <EmojiPicker
                                                    onEmojiClick={handleEmojiClick}
                                                    width={320}
                                                    height={400}
                                                    searchPlaceholder="Buscar emoji..."
                                                    previewConfig={{ showPreview: false }}
                                                />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                    <motion.button
                                        type="button"
                                        ref={emojiButtonRef}
                                        className={`emoji-btn ${showEmojiPicker ? 'active' : ''}`}
                                        onClick={() => { setShowEmojiPicker(prev => !prev); setShowGifPicker(false); }}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        title="Emojis"
                                    >
                                        <Smile size={20} />
                                    </motion.button>
                                </div>

                                {GIPHY_API_KEY && <div className="gif-picker-wrapper" ref={gifPickerRef}>
                                    <AnimatePresence>
                                        {showGifPicker && (
                                            <motion.div
                                                className="gif-picker-popover"
                                                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                                exit={{ opacity: 0, scale: 0.9, y: 10 }}
                                                transition={{ duration: 0.15 }}
                                            >
                                                <div className="gif-picker-header">
                                                    <Search size={16} />
                                                    <input
                                                        type="text"
                                                        placeholder="Buscar GIF..."
                                                        value={gifSearch}
                                                        onChange={(e) => handleGifSearch(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="gif-picker-grid">
                                                    {gifLoading ? (
                                                        <div className="gif-loading"><div className="spinner small" /></div>
                                                    ) : gifs.length === 0 ? (
                                                        <div className="gif-empty">No se encontraron GIFs</div>
                                                    ) : (
                                                        gifs.map((gif) => (
                                                            <img
                                                                key={gif.id}
                                                                src={gif.images?.fixed_height_small?.url || gif.images?.fixed_height?.url}
                                                                alt={gif.title || 'GIF'}
                                                                className="gif-item"
                                                                onClick={() => handleSendGif(gif)}
                                                                loading="lazy"
                                                            />
                                                        ))
                                                    )}
                                                </div>
                                                <div className="gif-picker-footer">
                                                    <img src="https://giphy.com/static/img/poweredby_giphy.png" alt="Powered by GIPHY" height="14" />
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                    <motion.button
                                        type="button"
                                        className={`gif-btn ${showGifPicker ? 'active' : ''}`}
                                        onClick={() => { setShowGifPicker(prev => !prev); setShowEmojiPicker(false); setShowStickerPicker(false); }}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        title="GIFs"
                                    >
                                        <span className="gif-btn-text">GIF</span>
                                    </motion.button>
                                </div>}

                                {GIPHY_API_KEY && <div className="gif-picker-wrapper" ref={stickerPickerRef}>
                                    <AnimatePresence>
                                        {showStickerPicker && (
                                            <motion.div
                                                className="gif-picker-popover sticker-picker-popover"
                                                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                                exit={{ opacity: 0, scale: 0.9, y: 10 }}
                                                transition={{ duration: 0.15 }}
                                            >
                                                <div className="gif-picker-header">
                                                    <Search size={16} />
                                                    <input
                                                        type="text"
                                                        placeholder="Buscar sticker..."
                                                        value={stickerSearch}
                                                        onChange={(e) => handleStickerSearch(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="gif-picker-grid sticker-picker-grid">
                                                    {stickerLoading ? (
                                                        <div className="gif-loading"><div className="spinner small" /></div>
                                                    ) : stickers.length === 0 ? (
                                                        <div className="gif-empty">No se encontraron stickers</div>
                                                    ) : (
                                                        stickers.map((s) => (
                                                            <img
                                                                key={s.id}
                                                                src={s.images?.fixed_height_small?.url || s.images?.fixed_height?.url}
                                                                alt={s.title || 'Sticker'}
                                                                className="gif-item sticker-item"
                                                                onClick={() => handleSendSticker(s)}
                                                                loading="lazy"
                                                            />
                                                        ))
                                                    )}
                                                </div>
                                                <div className="gif-picker-footer">
                                                    <img src="https://giphy.com/static/img/poweredby_giphy.png" alt="Powered by GIPHY" height="14" />
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                    <motion.button
                                        type="button"
                                        className={`gif-btn sticker-btn ${showStickerPicker ? 'active' : ''}`}
                                        onClick={() => { setShowStickerPicker(prev => !prev); setShowEmojiPicker(false); setShowGifPicker(false); }}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        title="Stickers"
                                    >
                                        <Sticker size={20} />
                                    </motion.button>
                                </div>}

                                {activeConversation?.is_group && (
                                    <motion.button
                                        type="button"
                                        className="attach-btn poll-btn"
                                        onClick={() => setShowPollCreator(true)}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        title="Crear encuesta"
                                    >
                                        <BarChart3 size={20} />
                                    </motion.button>
                                )}

                                {/* Boton crear ticket desde chat (solo admin en 1:1) */}
                                {(user?.role === 'admin' || user?.role === 'ti') && activeConversation && !activeConversation.is_group && (
                                    <motion.button
                                        type="button"
                                        className="attach-btn ticket-suggest-btn"
                                        onClick={handleSuggestTicket}
                                        disabled={creatingTicketFromChat}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                        title="Crear ticket desde esta conversacion"
                                    >
                                        {creatingTicketFromChat ? <Loader2 size={18} className="tk-spin" /> : <Ticket size={20} />}
                                    </motion.button>
                                )}

                                <div className="buzz-btn-wrapper">
                                    <motion.button
                                        type="button"
                                        className={`buzz-btn ${buzzCooldownRemaining > 0 ? 'on-cooldown' : ''}`}
                                        onClick={() => sendBuzz()}
                                        disabled={buzzCooldownRemaining > 0}
                                        whileHover={buzzCooldownRemaining > 0 ? {} : { scale: 1.15 }}
                                        whileTap={buzzCooldownRemaining > 0 ? {} : { scale: 0.85, rotate: [-8, 8, -8, 0] }}
                                        title={buzzCooldownRemaining > 0 ? `Espera ${buzzCooldownRemaining}s` : 'Enviar zumbido'}
                                    >
                                        <Zap size={20} />
                                    </motion.button>
                                    {buzzCooldownRemaining > 0 && (
                                        <svg className="buzz-cooldown-ring" viewBox="0 0 40 40">
                                            <circle
                                                cx="20" cy="20" r="18"
                                                fill="none"
                                                stroke="rgba(245, 158, 11, 0.15)"
                                                strokeWidth="2.5"
                                            />
                                            <circle
                                                cx="20" cy="20" r="18"
                                                fill="none"
                                                stroke="#f59e0b"
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeDasharray={`${2 * Math.PI * 18}`}
                                                strokeDashoffset={`${2 * Math.PI * 18 * (1 - buzzCooldownRemaining / 30)}`}
                                                transform="rotate(-90 20 20)"
                                                style={{ transition: 'stroke-dashoffset 1s linear' }}
                                            />
                                        </svg>
                                    )}
                                    {buzzCooldownRemaining > 0 && (
                                        <span className="buzz-cooldown-text">{buzzCooldownRemaining}</span>
                                    )}
                                </div>

                                <motion.button
                                    type="button"
                                    className={`send-btn ${(messageInput.trim() || selectedFiles.length > 0) ? 'active' : ''}`}
                                    onClick={handleSendMessage}
                                    disabled={uploading || (!messageInput.trim() && selectedFiles.length === 0)}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                >
                                    {uploading ? (
                                        <div className="spinner small" />
                                    ) : (
                                        <Send size={20} />
                                    )}
                                </motion.button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="no-chat-selected">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                        >
                            <MessageCircle size={80} strokeWidth={1} />
                            <h3>¡Bienvenido al Chat!</h3>
                            <p>Selecciona una conversación o inicia una nueva</p>
                            <button onClick={() => setShowNewChat(true)}>
                                <Plus size={18} />
                                Nuevo Chat
                            </button>
                        </motion.div>
                    </div>
                )}
            </main>

            {/* Modal de nuevo chat */}
            <AnimatePresence>
                {showNewChat && (
                    <motion.div
                        className="new-chat-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => { setShowNewChat(false); setNewChatSearch(''); }}
                    >
                        <motion.div
                            className="new-chat-modal"
                            initial={{ scale: 0.9, y: 50 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 50 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3><Plus size={20} /> Nuevo Chat</h3>
                                <button onClick={() => { setShowNewChat(false); setNewChatSearch(''); }}>
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="modal-search">
                                <Search size={18} />
                                <input
                                    type="text"
                                    placeholder="Buscar usuario..."
                                    value={newChatSearch}
                                    onChange={(e) => setNewChatSearch(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            <div className="users-list">
                                {availableUsers.filter(u =>
                                    !newChatSearch.trim() ||
                                    incluyeTexto(u.nombre, newChatSearch) ||
                                    incluyeTexto(u.username, newChatSearch)
                                ).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
                                .map((u) => (
                                    <motion.div
                                        key={u.id}
                                        className="user-item"
                                        onClick={() => handleStartConversation(u.id)}
                                        whileHover={{ x: 5, backgroundColor: 'rgba(34, 211, 238, 0.1)' }}
                                    >
                                        <div className="user-avatar">
                                            {u.foto ? (
                                                <>
                                                    <img 
                                                        src={u.foto} 
                                                        alt={u.nombre} 
                                                        className="avatar-img-chat" 
                                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} 
                                                    />
                                                    <span style={{ display: 'none' }}>{u.nombre?.charAt(0).toUpperCase()}</span>
                                                </>
                                            ) : (
                                                <span>{u.nombre?.charAt(0).toUpperCase()}</span>
                                            )}
                                        </div>
                                        <div className="user-info">
                                            <span className="user-name">{u.nombre}</span>
                                            <span className="user-role">{u.cargo || u.role} {u.area ? `• ${u.area}` : ''}</span>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Modal de crear grupo */}
            <AnimatePresence>
                {showCreateGroup && (
                    <motion.div
                        className="new-chat-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => {
                            setShowCreateGroup(false);
                            setGroupName('');
                            setSelectedMembers([]);
                            setGroupChatSearch('');
                        }}
                    >
                        <motion.div
                            className="new-chat-modal group-modal"
                            initial={{ scale: 0.9, y: 50 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 50 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3><Users size={20} /> Crear Grupo</h3>
                                <button onClick={() => {
                                    setShowCreateGroup(false);
                                    setGroupName('');
                                    setSelectedMembers([]);
                                    setGroupChatSearch('');
                                }}>
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="group-name-input">
                                <input
                                    type="text"
                                    placeholder="Nombre del grupo..."
                                    value={groupName}
                                    onChange={(e) => setGroupName(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            <div className="modal-search">
                                <Search size={18} />
                                <input
                                    type="text"
                                    placeholder="Buscar usuario..."
                                    value={groupChatSearch}
                                    onChange={(e) => setGroupChatSearch(e.target.value)}
                                />
                            </div>

                            <div className="modal-section-title">
                                Seleccionar miembros ({selectedMembers.length})
                            </div>

                            <div className="users-list">
                                {[...availableUsers].filter(u =>
                                    !groupChatSearch.trim() ||
                                    incluyeTexto(u.nombre, groupChatSearch) ||
                                    incluyeTexto(u.username, groupChatSearch)
                                ).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')).map((u) => (
                                    <motion.div
                                        key={u.id}
                                        className={`user-item selectable ${selectedMembers.includes(u.id) ? 'selected' : ''}`}
                                        onClick={() => toggleMemberSelection(u.id)}
                                        whileHover={{ x: 5, backgroundColor: 'rgba(34, 211, 238, 0.1)' }}
                                    >
                                        <div className="user-avatar">
                                            {u.foto ? (
                                                <>
                                                    <img 
                                                        src={u.foto} 
                                                        alt={u.nombre} 
                                                        className="avatar-img-chat" 
                                                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} 
                                                    />
                                                    <span style={{ display: 'none' }}>{u.nombre?.charAt(0).toUpperCase()}</span>
                                                </>
                                            ) : (
                                                <span>{u.nombre?.charAt(0).toUpperCase()}</span>
                                            )}
                                        </div>
                                        <div className="user-info">
                                            <span className="user-name">{u.nombre}</span>
                                            <span className="user-role">{u.cargo || u.role} {u.area ? `• ${u.area}` : ''}</span>
                                        </div>
                                        {selectedMembers.includes(u.id) && (
                                            <Check size={18} className="check-icon" />
                                        )}
                                    </motion.div>
                                ))}
                            </div>

                            <div className="modal-actions">
                                <button
                                    className="btn-create-group"
                                    onClick={handleCreateGroup}
                                    disabled={!groupName.trim() || selectedMembers.length === 0}
                                >
                                    <Users size={18} />
                                    Crear Grupo
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Panel de configuración de grupo */}
            <AnimatePresence>
                {showGroupSettings && activeConversation?.is_group && (
                    <motion.div
                        className="new-chat-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowGroupSettings(false)}
                    >
                        <motion.div
                            className="new-chat-modal group-settings-modal"
                            initial={{ scale: 0.9, y: 50 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 50 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3><Settings size={20} /> Configuración del Grupo</h3>
                                <button onClick={() => setShowGroupSettings(false)}>
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="group-info-section">
                                <div className="group-avatar large">
                                    <Users size={32} />
                                </div>
                                <h4>{activeConversation.name}</h4>
                                {activeConversation.is_owner && (
                                    <span className="owner-badge">
                                        <Crown size={14} /> Eres el administrador
                                    </span>
                                )}
                            </div>

                            <div className="modal-section-title">
                                Miembros ({activeConversation.participants?.length || 0})
                            </div>

                            <div className="users-list members-list">
                                {activeConversation.participants?.map((p) => (
                                    <div key={p.id} className="user-item member-item">
                                        <div className="user-avatar">
                                            <span>{p.nombre?.charAt(0).toUpperCase()}</span>
                                        </div>
                                        <div className="user-info">
                                            <span className="user-name">
                                                {p.nombre}
                                                {p.id === activeConversation.owner_id && (
                                                    <Crown size={14} className="owner-icon" />
                                                )}
                                            </span>
                                            <span className="user-role">{p.role}</span>
                                        </div>
                                        <div className="member-actions">
                                            {/* Owner can make others admin */}
                                            {activeConversation.is_owner && p.id !== activeConversation.owner_id && (
                                                <>
                                                    {transferConfirm === p.id ? (
                                                        <div className="transfer-confirm">
                                                            <span className="transfer-confirm-text">Transferir admin?</span>
                                                            <button
                                                                className="btn-confirm-yes"
                                                                onClick={() => handleTransferAdmin(p.id)}
                                                                title="Confirmar"
                                                            >
                                                                <Check size={14} />
                                                            </button>
                                                            <button
                                                                className="btn-confirm-no"
                                                                onClick={() => setTransferConfirm(null)}
                                                                title="Cancelar"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <button
                                                                className="btn-transfer-admin"
                                                                onClick={() => setTransferConfirm(p.id)}
                                                                title="Hacer administrador"
                                                            >
                                                                <ShieldCheck size={16} />
                                                            </button>
                                                            <button
                                                                className="btn-remove-member"
                                                                onClick={() => handleRemoveFromGroup(p.id)}
                                                                title="Remover del grupo"
                                                            >
                                                                <UserMinus size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {activeConversation.is_owner && (
                                <div className="modal-actions">
                                    <button
                                        className="btn-invite"
                                        onClick={() => {
                                            setShowGroupSettings(false);
                                            setShowInviteModal(true);
                                        }}
                                    >
                                        <UserPlus size={18} />
                                        Invitar usuarios
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Modal de invitar usuarios */}
            <AnimatePresence>
                {showInviteModal && activeConversation?.is_group && (
                    <motion.div
                        className="new-chat-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => {
                            setShowInviteModal(false);
                            setSelectedMembers([]);
                            setInviteSearch('');
                        }}
                    >
                        <motion.div
                            className="new-chat-modal"
                            initial={{ scale: 0.9, y: 50 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 50 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3><UserPlus size={20} /> Invitar al Grupo</h3>
                                <button onClick={() => {
                                    setShowInviteModal(false);
                                    setSelectedMembers([]);
                                    setInviteSearch('');
                                }}>
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="modal-search">
                                <Search size={18} />
                                <input
                                    type="text"
                                    placeholder="Buscar usuario..."
                                    value={inviteSearch}
                                    onChange={(e) => setInviteSearch(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            <div className="modal-section-title">
                                Seleccionar usuarios ({selectedMembers.length})
                            </div>

                            <div className="users-list">
                                {availableUsers
                                    .filter(u => !activeConversation.participants?.some(p => p.id === u.id))
                                    .filter(u =>
                                        !inviteSearch.trim() ||
                                        incluyeTexto(u.nombre, inviteSearch) ||
                                        incluyeTexto(u.username, inviteSearch) ||
                                        incluyeTexto(u.cargo, inviteSearch) ||
                                        incluyeTexto(u.area, inviteSearch)
                                    )
                                    .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
                                    .map((u) => (
                                        <motion.div
                                            key={u.id}
                                            className={`user-item selectable ${selectedMembers.includes(u.id) ? 'selected' : ''}`}
                                            onClick={() => toggleMemberSelection(u.id)}
                                            whileHover={{ x: 5, backgroundColor: 'rgba(34, 211, 238, 0.1)' }}
                                        >
                                            <div className="user-avatar">
                                                {u.foto ? (
                                                    <>
                                                        <img
                                                            src={u.foto}
                                                            alt={u.nombre}
                                                            className="avatar-img-chat"
                                                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                        />
                                                        <span style={{ display: 'none' }}>{u.nombre?.charAt(0).toUpperCase()}</span>
                                                    </>
                                                ) : (
                                                    <span>{u.nombre?.charAt(0).toUpperCase()}</span>
                                                )}
                                            </div>
                                            <div className="user-info">
                                                <span className="user-name">{u.nombre}</span>
                                                <span className="user-role">{u.cargo || u.role} {u.area ? `• ${u.area}` : ''}</span>
                                            </div>
                                            {selectedMembers.includes(u.id) && (
                                                <Check size={18} className="check-icon" />
                                            )}
                                        </motion.div>
                                    ))}
                            </div>

                            <div className="modal-actions">
                                <button
                                    className="btn-invite"
                                    onClick={() => handleInviteToGroup(selectedMembers)}
                                    disabled={selectedMembers.length === 0}
                                >
                                    <UserPlus size={18} />
                                    Invitar ({selectedMembers.length})
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* File Viewer Modal */}
            {viewerFile && (
                <FileViewer
                    file={viewerFile.file}
                    senderName={viewerFile.senderName}
                    timestamp={viewerFile.timestamp}
                    baseUrl={BASE_URL}
                    allFiles={conversationFiles}
                    currentIndex={viewerFile.index}
                    onClose={() => setViewerFile(null)}
                    conversations={conversations}
                    onlineUsers={onlineUsers}
                    userId={user?.id}
                    onForward={async (file, conversationId) => {
                        // Reenviar: subir de nuevo como nuevo mensaje en la conversación destino
                        try {
                            const token = localStorage.getItem('token');
                            // Descargar el archivo original
                            const response = await fetch(`${BASE_URL}${file.download_url}?token=${token}`);
                            if (!response.ok) return;
                            const blob = await response.blob();
                            const newFile = new window.File([blob], file.original_name, { type: file.mime_type });
                            const uploaded = await uploadFile(newFile);
                            if (uploaded) {
                                const msgResponse = await fetch(`${API_URL}/chat/conversations/${conversationId}/messages`, {
                                    method: 'POST',
                                    headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({ file_id: uploaded.id })
                                });
                                if (msgResponse.ok) {
                                    setViewerFile(null);
                                }
                            }
                        } catch (error) {
                            console.error('Error forwarding file:', error);
                        }
                    }}
                />
            )}

            {/* Modal de personalización */}
            <AnimatePresence>
                {showCustomize && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowCustomize(false)}
                    >
                        <motion.div
                            className="modal-content customize-modal"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3><Palette size={18} /> Personalizar chat</h3>
                                <button onClick={() => setShowCustomize(false)}><X size={20} /></button>
                            </div>
                            <div className="customize-body">
                                <div className="customize-section">
                                    <label>Color de tus burbujas</label>
                                    <div className="color-options">
                                        {BUBBLE_COLORS.map(c => (
                                            <button
                                                key={c.key}
                                                className={`color-swatch ${chatPrefs.bubbleColor === c.key || (!chatPrefs.bubbleColor && c.key === 'default') ? 'active' : ''}`}
                                                style={{ background: c.bg }}
                                                onClick={() => updateChatPrefs('bubbleColor', c.key)}
                                                title={c.label}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="customize-section">
                                    <label>Color de burbuja del otro</label>
                                    <div className="color-options">
                                        {BUBBLE_COLORS.map(c => (
                                            <button
                                                key={c.key}
                                                className={`color-swatch ${chatPrefs.incomingBubbleColor === c.key || (!chatPrefs.incomingBubbleColor && c.key === 'gray') ? 'active' : ''}`}
                                                style={{ background: c.bg }}
                                                onClick={() => updateChatPrefs('incomingBubbleColor', c.key)}
                                                title={c.label}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="customize-section">
                                    <label>Forma de las burbujas</label>
                                    <div className="font-options">
                                        {BUBBLE_SHAPES.map(s => (
                                            <button
                                                key={s.key}
                                                className={`font-option ${chatPrefs.bubbleShape === s.key || (!chatPrefs.bubbleShape && s.key === 'tail') ? 'active' : ''}`}
                                                onClick={() => updateChatPrefs('bubbleShape', s.key)}
                                            >
                                                <div
                                                    className={`shape-preview shape-preview-${s.key}`}
                                                    style={{ background: 'var(--bubble-bg, var(--bubble-color, #0891b2))' }}
                                                />
                                                <span className="font-label">{s.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="customize-section">
                                    <label>Fondo del chat</label>
                                    <div className="bg-options">
                                        {CHAT_BACKGROUNDS.map(bg => (
                                            <button
                                                key={bg.key}
                                                className={`bg-swatch ${chatPrefs.background === bg.key || (!chatPrefs.background && bg.key === 'none') ? 'active' : ''}`}
                                                onClick={() => updateChatPrefs('background', bg.key)}
                                            >
                                                <div className="bg-preview" style={bg.style} />
                                                <span>{bg.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="customize-section">
                                    <label>Tamaño de letra</label>
                                    <div className="font-options">
                                        {FONT_SIZES.map(f => (
                                            <button
                                                key={f.key}
                                                className={`font-option ${chatPrefs.fontSize === f.key || (!chatPrefs.fontSize && f.key === 'normal') ? 'active' : ''}`}
                                                onClick={() => updateChatPrefs('fontSize', f.key)}
                                            >
                                                <span style={{ fontSize: f.size }}>Aa</span>
                                                <span className="font-label">{f.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Modal de creación de encuesta */}
            <AnimatePresence>
                {showPollCreator && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowPollCreator(false)}
                    >
                        <motion.div
                            className="modal-content poll-creator-modal"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3><BarChart3 size={18} /> Crear encuesta</h3>
                                <button onClick={() => setShowPollCreator(false)}><X size={20} /></button>
                            </div>
                            <div className="poll-creator-body">
                                <input
                                    className="poll-question-input"
                                    type="text"
                                    placeholder="¿Cuál es tu pregunta?"
                                    value={pollQuestion}
                                    onChange={(e) => setPollQuestion(e.target.value)}
                                    maxLength={500}
                                    autoFocus
                                />
                                <div className="poll-options-list">
                                    {pollOptions.map((opt, idx) => (
                                        <div key={idx} className="poll-option-input-row">
                                            <input
                                                type="text"
                                                placeholder={`Opción ${idx + 1}`}
                                                value={opt}
                                                onChange={(e) => {
                                                    const updated = [...pollOptions];
                                                    updated[idx] = e.target.value;
                                                    setPollOptions(updated);
                                                }}
                                                maxLength={200}
                                            />
                                            {pollOptions.length > 2 && (
                                                <button className="poll-remove-option" onClick={() => {
                                                    setPollOptions(prev => prev.filter((_, i) => i !== idx));
                                                }}>
                                                    <X size={14} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    {pollOptions.length < 10 && (
                                        <button
                                            className="poll-add-option"
                                            onClick={() => setPollOptions(prev => [...prev, ''])}
                                        >
                                            <Plus size={14} /> Agregar opción
                                        </button>
                                    )}
                                </div>
                                <button
                                    className="poll-submit-btn"
                                    disabled={!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2}
                                    onClick={() => {
                                        createPoll(pollQuestion, pollOptions.filter(o => o.trim()));
                                        setShowPollCreator(false);
                                        setPollQuestion('');
                                        setPollOptions(['', '']);
                                    }}
                                >
                                    Crear encuesta
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Modal de resumen IA */}
            <AnimatePresence>
                {(showSummary !== null || summaryLoading) && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => { setShowSummary(null); setSummaryLoading(false); }}
                    >
                        <motion.div
                            className="modal-content summary-modal"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3>Ponme al día</h3>
                                <button onClick={() => { setShowSummary(null); setSummaryLoading(false); }}><X size={20} /></button>
                            </div>
                            <div className="summary-period-tabs">
                                {[
                                    { key: '1h', label: 'Última hora' },
                                    { key: '24h', label: 'Últimas 24h' },
                                    { key: '7d', label: 'Últimos 7 días' }
                                ].map(tab => (
                                    <button
                                        key={tab.key}
                                        className={`summary-period-tab ${summaryPeriod === tab.key ? 'active' : ''}`}
                                        onClick={() => handleGetSummary(tab.key)}
                                        disabled={summaryLoading}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                            <div className="summary-content">
                                {summaryLoading ? (
                                    <AiLoadingOverlay
                                        inline
                                        size="sm"
                                        phases={['Leyendo mensajes...', 'Analizando conversación...', 'Generando resumen...']}
                                    />
                                ) : showSummary ? (
                                    <>
                                        <div
                                            className="summary-text"
                                            dangerouslySetInnerHTML={{
                                                __html: showSummary
                                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                    .replace(/^[-•]\s+(.*)$/gm, '<li>$1</li>')
                                                    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
                                                    .replace(/\n{2,}/g, '<br/><br/>')
                                                    .replace(/\n/g, '<br/>')
                                            }}
                                        />
                                        {summaryMeta?.count && (
                                            <span className="summary-meta">{summaryMeta.count} mensajes analizados</span>
                                        )}
                                    </>
                                ) : (
                                    <div className="summary-placeholder">
                                        Selecciona un período para generar el resumen
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Modal de reenvío de mensaje */}
            <AnimatePresence>
                {forwardingMessage && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setForwardingMessage(null)}
                    >
                        <motion.div
                            className="modal-content forward-modal"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3>Reenviar mensaje</h3>
                                <button onClick={() => setForwardingMessage(null)}><X size={20} /></button>
                            </div>
                            <div className="forward-preview">
                                <p>{forwardingMessage.content
                                    ? (isBuzzMessage(forwardingMessage.content) ? '⚡ Zumbido' : isGifMessage(forwardingMessage.content) ? 'GIF' : isStickerMessage(forwardingMessage.content) ? 'Sticker' : isTicketSuggestMessage(forwardingMessage.content) ? '🎫 Sugerencia de ticket' : isTicketCreatedMessage(forwardingMessage.content) ? '🎫 Ticket creado' : forwardingMessage.content.substring(0, 100))
                                    : forwardingMessage.file ? forwardingMessage.file.original_name : ''}</p>
                            </div>
                            <div className="chat-search">
                                <Search size={18} />
                                <input
                                    type="text"
                                    placeholder="Buscar conversación..."
                                    value={forwardSearch}
                                    onChange={(e) => setForwardSearch(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div className="forward-conversation-list">
                                {conversations
                                    .filter(c => c.id !== activeConversation?.id && incluyeTexto(c.name, forwardSearch))
                                    .map(conv => (
                                        <div
                                            key={conv.id}
                                            className="forward-conversation-item"
                                            onClick={() => {
                                                forwardMessage(forwardingMessage.id, conv.id);
                                                setForwardingMessage(null);
                                            }}
                                        >
                                            <div className="conversation-avatar small">
                                                {conv.is_group ? <Users size={16} /> : <span>{conv.name?.charAt(0).toUpperCase()}</span>}
                                            </div>
                                            <span>{conv.name}</span>
                                        </div>
                                    ))}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Preview de ticket sugerido (solo TI) */}
            <AnimatePresence>
                {ticketPreview && (() => {
                    const catLabels = { hardware: 'Hardware', software: 'Software', red: 'Red', accesos: 'Accesos', otro: 'Otro' };
                    const prioLabels = { baja: 'Baja', media: 'Media', alta: 'Alta', critica: 'Critica' };
                    const prioColors = { baja: '#64748b', media: '#3b82f6', alta: '#f59e0b', critica: '#ef4444' };
                    return (
                        <motion.div
                            className="modal-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => !creatingTicketFromChat && setTicketPreview(null)}
                        >
                            <motion.div
                                className="modal-content"
                                style={{ maxWidth: 480 }}
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="modal-header">
                                    <h3>Vista previa del ticket</h3>
                                    <button onClick={() => setTicketPreview(null)} disabled={creatingTicketFromChat}><X size={20} /></button>
                                </div>
                                <div className="ticket-suggest-card" style={{ margin: 16 }}>
                                    <div className="tsc-header">
                                        <Ticket size={16} />
                                        <span>Sugerencia generada por IA</span>
                                    </div>
                                    <h4 className="tsc-title">{ticketPreview.titulo}</h4>
                                    <p className="tsc-desc">{ticketPreview.descripcion}</p>
                                    <div className="tsc-badges">
                                        <span className="tsc-badge" style={{ color: '#0891b2', background: '#ecfeff' }}>
                                            <Monitor size={12} /> {catLabels[ticketPreview.categoria] || ticketPreview.categoria}
                                        </span>
                                        <span className="tsc-badge" style={{ color: prioColors[ticketPreview.prioridad] || '#64748b', background: `${prioColors[ticketPreview.prioridad] || '#64748b'}15` }}>
                                            {prioLabels[ticketPreview.prioridad] || ticketPreview.prioridad}
                                        </span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 8, padding: '0 16px 16px', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={() => setTicketPreview(null)}
                                        disabled={creatingTicketFromChat}
                                        style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', fontWeight: 500 }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleConfirmTicketPreview}
                                        disabled={creatingTicketFromChat}
                                        className="tsc-create-btn"
                                        style={{ width: 'auto' }}
                                    >
                                        {creatingTicketFromChat ? <Loader2 size={15} className="tk-spin" /> : <Ticket size={15} />}
                                        {creatingTicketFromChat ? 'Creando…' : 'Crear ticket'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    );
                })()}
            </AnimatePresence>

            {/* Modal de estado de presencia */}
            <AnimatePresence>
                {showPresenceMenu && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowPresenceMenu(false)}
                    >
                        <motion.div
                            className="modal-content presence-modal"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3>Estado de presencia</h3>
                                <button onClick={() => setShowPresenceMenu(false)}><X size={20} /></button>
                            </div>
                            <div className="presence-options">
                                {[
                                    { key: 'disponible', label: 'Disponible', color: '#22c55e' },
                                    { key: 'reunion', label: 'En reunión', color: '#f59e0b' },
                                    { key: 'almorzando', label: 'Almorzando', color: '#f97316' },
                                    { key: 'no_molestar', label: 'No molestar', color: '#ef4444' },
                                ].map(opt => (
                                    <button
                                        key={opt.key}
                                        className={`presence-option ${userPresence[user?.id] === opt.key || (!userPresence[user?.id] && opt.key === 'disponible') ? 'active' : ''}`}
                                        onClick={() => {
                                            setPresenceStatus(opt.key);
                                            setShowPresenceMenu(false);
                                        }}
                                    >
                                        <Circle size={10} fill={opt.color} color={opt.color} />
                                        <span>{opt.label}</span>
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ChatView;
